import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, LoadScriptNext, Marker } from "@react-google-maps/api";
import { useHistory, useLocation, useParams } from "react-router-dom";
import styles from "./campaigns.page.module.scss";
import { sanitizeHtml } from "../utils/sanitizeHtml";
import env from "environments/env";
import { Path } from "enums";
import {
  getTotalProcessedResponses,
  getCategoryDistribution,
  getSentimentDistribution,
  getEmploymentSectorDistribution,
  getEnumeratorPerformance,
  getKeyConcerns,
  getAgeDistribution,
  getSexDistribution,
} from "apis/survey-analytics.api";
import {
  getFieldResponseProcessedFilterOptions,
  getFieldResponsesProcessed,
  deleteFieldResponseProcessed,
  getFieldResponseProcessedTranscript,
  updateFieldResponseProcessedTranscriptRow,
  restoreFieldResponseProcessedTranscriptRow,
  restoreFieldResponseProcessedTranscript,
  updateFieldResponseProcessedIdentity,
} from "apis/field-response-processed.api";
import {
  getCampaigns as getCampaignsRequest,
  createCampaign as createCampaignRequest,
  endCampaign as endCampaignRequest,
  getCampaignByCode as getCampaignByCodeRequest,
  getCampaignQuestions as getCampaignQuestionsRequest,
  getGeographyProvinces,
  getGeographyMunicipalities,
} from "apis/campaign.api";
import {
  createChatThread,
  getChatThreads,
  getChatThreadMessages,
  createChatThreadMessage,
} from "apis/chat.api";
import ExecutiveSummaryUpload from "components/commons/executive-summary-upload/executive-summary-upload";

const RECORDINGS_API_PER_PAGE = 20;

const statCards = [
  { label: "Total Campaigns", value: "3", tone: "default" },
  { label: "Active", value: "3", tone: "active" },
  { label: "Total Recordings", value: "0", tone: "info" },
  { label: "Enumerators", value: "0", tone: "accent" },
];

const EMPTY_CAMPAIGN_DETAILS = {
  totalRecordings: 0,
  enumeratorPerformance: [],
  ageDistribution: [],
  genderDistribution: [],
  recordings: [],
};

const formatCampaignDate = (value) => {
  if (!value) return "-";
  const asString = String(value);
  return asString.includes("T") ? asString.split("T")[0] : asString;
};

const formatExportDateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) return null;

  const pad = (part) => String(part).padStart(2, "0");
  return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(
    parsed.getHours(),
  )}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
};

const toCampaignDateISO = (value) => {
  const [year, month, day] = String(value || "")
    .split("-")
    .map((part) => Number(part));
  return new Date(Date.UTC(year, month - 1, day)).toISOString();
};

const toAgeNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value.replace(/[^\d]/g, ""), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const toAgeGroupFromAge = (value) => {
  const age = toAgeNumber(value);
  if (age == null || age <= 0) return "";
  if (age <= 17) return "17 and below";
  if (age <= 24) return "18-24";
  if (age <= 34) return "25-34";
  if (age <= 44) return "35-44";
  if (age <= 54) return "45-54";
  if (age <= 64) return "55-64";
  return "65+";
};

const AGE_DISTRIBUTION_BUCKETS = ["0-17", "18-29", "30-44", "45-59", "60+", "Unknown"];

const resolveAgeDistributionBucket = (value) => {
  const raw = String(value ?? "")
    .trim()
    .toLowerCase();
  if (!raw) return "Unknown";

  if (/(unknown|n\/a|na|not\s+specified|prefer\s+not|decline)/.test(raw)) {
    return "Unknown";
  }

  const values = raw.match(/\d+/g) || [];
  const numbers = values.map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry));

  if (numbers.length === 0) {
    return "Unknown";
  }

  let representativeAge;
  if (numbers.length >= 2) {
    representativeAge = (numbers[0] + numbers[1]) / 2;
  } else {
    representativeAge = numbers[0];
  }

  if (representativeAge <= 17.5) return "0-17";
  if (representativeAge <= 29.5) return "18-29";
  if (representativeAge <= 44.5) return "30-44";
  if (representativeAge <= 59.5) return "45-59";
  return "60+";
};

const normalizeCampaign = (campaign) => {
  const code = campaign?.campaignCode || campaign?.code || "";
  const rawSurveyType = String(campaign?.surveyType || campaign?.type || "single").toLowerCase();
  const surveyType = rawSurveyType === "custom" ? "custom" : "single";
  return {
    campaignId: campaign?.campaignId,
    code,
    status: String(campaign?.status || "inactive").toLowerCase(),
    title: campaign?.description || campaign?.name || code,
    date: formatCampaignDate(campaign?.campaignDate || campaign?.date),
    location: campaign?.location || "Unknown",
    surveyType,
    surveyQuestionDocument: campaign?.surveyQuestionDocument || "",
    enumerators: 0,
    recordingsSummary: "0 total",
    pos: 0,
    neg: 0,
    neutral: 0,
    topConcern: "—",
    avgSentiment: 0,
    details: EMPTY_CAMPAIGN_DETAILS,
  };
};

const sentimentColor = (s) => {
  if (s === "positive") return "#34d399";
  if (s === "negative") return "#f87171";
  return "#fbbf24";
};

const toFiniteNumberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const cloneTranscriptLines = (lines) =>
  (Array.isArray(lines) ? lines : []).map((line, index) => ({
    id: line?.id || line?.segmentIndex || `line-${index}`,
    segmentIndex:
      line?.segmentIndex != null && Number.isFinite(Number(line.segmentIndex))
        ? Number(line.segmentIndex)
        : index,
    speaker: String(line?.speaker || "speaker_0").trim(),
    role: line?.role || null,
    text: String(line?.text || ""),
    originalText: String(line?.originalText ?? line?.text ?? ""),
    isEdited: Boolean(line?.isEdited),
    startMs: toFiniteNumberOrNull(line?.startMs),
    endMs: toFiniteNumberOrNull(line?.endMs),
  }));

const toTranscriptLineFromString = (line, index) => {
  const normalized = String(line || "").trim();
  if (!normalized) return null;

  const speakerMatch = normalized.match(/^([a-zA-Z0-9_]+)\s*:\s*(.*)$/);
  const speaker = speakerMatch ? speakerMatch[1] : `speaker_${index % 2}`;
  const text = speakerMatch ? speakerMatch[2] : normalized;

  return {
    id: `line-${index}`,
    segmentIndex: index,
    speaker: String(speaker || `speaker_${index % 2}`).trim(),
    role: null,
    text: String(text || "").trim(),
    originalText: String(text || "").trim(),
    isEdited: false,
    startMs: null,
    endMs: null,
  };
};

const toTranscriptLineFromObject = (line, index) => {
  if (!line || typeof line !== "object") return null;

  // Handle object-map lines like { speaker_0: "hello" }
  const speakerMapEntry = Object.entries(line).find(
    ([key, value]) => /^speaker[_-]?\w+/i.test(String(key || "")) && typeof value === "string",
  );
  if (speakerMapEntry) {
    const [speakerKey, speakerText] = speakerMapEntry;
    const normalizedText = String(speakerText || "").trim();
    if (!normalizedText) return null;

    return {
      id: String(line?.id || line?.segmentIndex || `line-${index}`),
      segmentIndex:
        line?.segmentIndex != null && Number.isFinite(Number(line.segmentIndex))
          ? Number(line.segmentIndex)
          : index,
      speaker: String(speakerKey || `speaker_${index % 2}`).trim(),
      role: line?.role || null,
      text: normalizedText,
      originalText: String(line?.originalText ?? normalizedText),
      isEdited: Boolean(line?.isEdited),
      startMs: toFiniteNumberOrNull(line?.startMs ?? line?.start_ms ?? line?.start),
      endMs: toFiniteNumberOrNull(line?.endMs ?? line?.end_ms ?? line?.end),
    };
  }

  const speaker =
    line?.speaker ||
    line?.speakerTag ||
    line?.speaker_label ||
    line?.speakerName ||
    line?.role ||
    line?.label ||
    line?.name ||
    `speaker_${index % 2}`;
  const rawText =
    line?.text ??
    line?.line ??
    line?.content ??
    line?.transcript ??
    line?.transcription ??
    line?.message ??
    line?.utterance ??
    line?.sentence ??
    line?.value ??
    "";
  const text = String(rawText || "").trim();
  if (!text) return null;

  return {
    id: String(line?.id || line?.segmentIndex || `line-${index}`),
    segmentIndex:
      line?.segmentIndex != null && Number.isFinite(Number(line.segmentIndex))
        ? Number(line.segmentIndex)
        : index,
    speaker: String(speaker || `speaker_${index % 2}`).trim(),
    role: line?.role || null,
    text,
    originalText: String(line?.originalText ?? text),
    isEdited: Boolean(line?.isEdited),
    startMs: toFiniteNumberOrNull(line?.startMs ?? line?.start_ms ?? line?.start),
    endMs: toFiniteNumberOrNull(line?.endMs ?? line?.end_ms ?? line?.end),
  };
};

const dedupeTranscriptLines = (lines) => {
  const deduped = [];
  const usedSegmentIndexes = new Set();

  for (const line of Array.isArray(lines) ? lines : []) {
    if (!line) continue;

    const segmentIndex = toFiniteNumberOrNull(line?.segmentIndex);
    if (segmentIndex != null) {
      if (usedSegmentIndexes.has(segmentIndex)) continue;
      usedSegmentIndexes.add(segmentIndex);
    }

    const previous = deduped[deduped.length - 1];
    const hasSameSpeaker =
      String(previous?.speaker || "").trim() === String(line?.speaker || "").trim();
    const hasSameText = String(previous?.text || "").trim() === String(line?.text || "").trim();
    const hasSameStart =
      toFiniteNumberOrNull(previous?.startMs) === toFiniteNumberOrNull(line?.startMs);
    const hasSameEnd = toFiniteNumberOrNull(previous?.endMs) === toFiniteNumberOrNull(line?.endMs);

    // Some transcript APIs repeat the same parsed row back-to-back.
    if (previous && hasSameSpeaker && hasSameText && hasSameStart && hasSameEnd) continue;

    deduped.push(line);
  }

  return deduped;
};

const normalizeTranscriptLines = (payload, fallbackTranscript) => {
  const source = payload?.data || payload || {};

  const candidateContainers = [
    source,
    source?.data,
    source?.result,
    source?.payload,
    source?.response,
    source?.item,
    source?.transcript,
  ].filter(Boolean);

  const parseLineArray = (items) => {
    if (!Array.isArray(items)) return [];

    return items
      .map((line, index) => {
        if (typeof line === "string") return toTranscriptLineFromString(line, index);
        return toTranscriptLineFromObject(line, index);
      })
      .filter(Boolean);
  };

  for (const container of candidateContainers) {
    const lineCandidates =
      container?.rows ||
      container?.lines ||
      container?.transcriptLines ||
      container?.transcript_rows ||
      container?.transcriptRows ||
      container?.items ||
      container?.transcript_items ||
      container?.segments ||
      container?.sentences ||
      container?.entries ||
      null;

    const parsed = parseLineArray(lineCandidates);
    if (parsed.length) return dedupeTranscriptLines(parsed);
  }

  // Handle object-map transcript payloads like { speaker_0: "...", speaker_1: "..." }
  for (const container of candidateContainers) {
    if (!container || typeof container !== "object" || Array.isArray(container)) continue;

    const speakerEntries = Object.entries(container).filter(
      ([key, value]) => /^speaker[_-]?\w+/i.test(String(key || "")) && typeof value === "string",
    );

    if (speakerEntries.length) {
      return dedupeTranscriptLines(
        speakerEntries
          .map(([speaker, text], index) => ({
            id: `line-${index}`,
            segmentIndex: index,
            speaker: String(speaker || `speaker_${index % 2}`).trim(),
            role: null,
            text: String(text || "").trim(),
            originalText: String(text || "").trim(),
            isEdited: false,
          }))
          .filter((line) => Boolean(line.text)),
      );
    }
  }

  const transcriptTextCandidate =
    source?.transcript ||
    source?.transcription ||
    source?.fullTranscript ||
    source?.text ||
    source?.rawTranscript ||
    source?.raw_transcript ||
    source?.data?.transcript ||
    source?.result?.transcript ||
    fallbackTranscript ||
    "";

  const transcriptText = Array.isArray(transcriptTextCandidate)
    ? transcriptTextCandidate
        .map((item) =>
          typeof item === "string"
            ? item
            : item && typeof item === "object"
              ? item?.text || item?.line || item?.content || item?.transcript || ""
              : "",
        )
        .filter(Boolean)
        .join("\n")
    : transcriptTextCandidate;

  return dedupeTranscriptLines(
    String(transcriptText)
      .split(/\r?\n/)
      .map((line, index) => toTranscriptLineFromString(line, index))
      .filter(Boolean),
  );
};

const serializeTranscriptLines = (lines) =>
  (Array.isArray(lines) ? lines : [])
    .map((line) => {
      const speaker = String(line?.speaker || "").trim();
      const text = String(line?.text || "").trim();
      return speaker ? `${speaker}: ${text}` : text;
    })
    .filter(Boolean)
    .join("\n");

const countEditedTranscriptLines = (lines) =>
  (Array.isArray(lines) ? lines : []).filter(
    (line) => String(line?.text || "") !== String(line?.originalText || ""),
  ).length;

const resolveTranscriptRowPayload = (payload) => {
  const source = payload?.data || payload || {};
  if (source?.row && typeof source.row === "object") return source.row;
  if (source?.data && typeof source.data === "object") return source.data;
  return source;
};

const normalizeTranscriptGroups = (payload) => {
  const source = payload?.data || payload || {};

  const candidateContainers = [
    source,
    source?.data,
    source?.result,
    source?.payload,
    source?.response,
    source?.item,
  ].filter(Boolean);

  let grouped = false;
  let groupsRaw = [];

  for (const container of candidateContainers) {
    if (typeof container?.grouped === "boolean") {
      grouped = container.grouped;
      if (Array.isArray(container?.groups)) {
        groupsRaw = container.groups;
      }
      break;
    }
  }

  const groups = Array.isArray(groupsRaw)
    ? groupsRaw.map((group, index) => ({
        id: String(
          group?.id ||
            `group-${index}-${group?.sequence ?? "orphan"}-${group?.firstSegmentIndex ?? "n"}`,
        ),
        sequence: toFiniteNumberOrNull(group?.sequence),
        campaignQuestionId: toFiniteNumberOrNull(group?.campaignQuestionId),
        questionType: group?.questionType ? String(group.questionType) : "",
        promptSnapshot: group?.promptSnapshot ? String(group.promptSnapshot) : "",
        answerSnapshot: group?.answerSnapshot ? String(group.answerSnapshot) : "",
        startMs: toFiniteNumberOrNull(group?.startMs),
        endMs: toFiniteNumberOrNull(group?.endMs),
        firstSegmentIndex: toFiniteNumberOrNull(group?.firstSegmentIndex),
        lastSegmentIndex: toFiniteNumberOrNull(group?.lastSegmentIndex),
        contextSegmentIndexes: Array.isArray(group?.contextSegmentIndexes)
          ? group.contextSegmentIndexes
              .map((value) => toFiniteNumberOrNull(value))
              .filter((value) => value != null)
          : [],
      }))
    : [];

  const sequencedGroups = groups
    .filter((group) => Number.isFinite(group?.sequence))
    .sort((a, b) => Number(a.sequence) - Number(b.sequence));
  const orphanGroups = groups.filter((group) => !Number.isFinite(group?.sequence));

  if (!sequencedGroups.length) {
    orphanGroups.sort((a, b) => {
      const aStart =
        toFiniteNumberOrNull(a?.firstSegmentIndex) ??
        toFiniteNumberOrNull(a?.lastSegmentIndex) ??
        Number.MAX_SAFE_INTEGER;
      const bStart =
        toFiniteNumberOrNull(b?.firstSegmentIndex) ??
        toFiniteNumberOrNull(b?.lastSegmentIndex) ??
        Number.MAX_SAFE_INTEGER;
      return aStart - bStart;
    });

    return {
      grouped: Boolean(grouped),
      groups: orphanGroups,
    };
  }

  const firstQuestionIndexes = sequencedGroups
    .map((group) => toFiniteNumberOrNull(group?.firstSegmentIndex))
    .filter((value) => value != null);
  const lastQuestionIndexes = sequencedGroups
    .map((group) => toFiniteNumberOrNull(group?.lastSegmentIndex))
    .filter((value) => value != null);
  const hasFiniteQuestionBounds = firstQuestionIndexes.length > 0 && lastQuestionIndexes.length > 0;
  const firstQuestionIndex = hasFiniteQuestionBounds
    ? Math.min(...firstQuestionIndexes)
    : Number.POSITIVE_INFINITY;
  const lastQuestionIndex = hasFiniteQuestionBounds
    ? Math.max(...lastQuestionIndexes)
    : Number.NEGATIVE_INFINITY;

  const orphanWithStart = orphanGroups.map((group, index) => ({
    group,
    originalIndex: index,
    start:
      toFiniteNumberOrNull(group?.firstSegmentIndex) ??
      toFiniteNumberOrNull(group?.lastSegmentIndex) ??
      Number.MAX_SAFE_INTEGER,
  }));

  const sortedOrphans = [...orphanWithStart].sort(
    (a, b) => a.start - b.start || a.originalIndex - b.originalIndex,
  );

  const headOrphans = hasFiniteQuestionBounds
    ? sortedOrphans
        .filter((entry) => Number.isFinite(entry.start) && entry.start < firstQuestionIndex)
        .map((entry) => entry.group)
    : [];
  const middleOrphans = hasFiniteQuestionBounds
    ? sortedOrphans
        .filter(
          (entry) =>
            Number.isFinite(entry.start) &&
            entry.start >= firstQuestionIndex &&
            entry.start <= lastQuestionIndex,
        )
        .map((entry) => entry.group)
    : [];
  const tailOrphans = hasFiniteQuestionBounds
    ? sortedOrphans
        .filter((entry) => !Number.isFinite(entry.start) || entry.start > lastQuestionIndex)
        .map((entry) => entry.group)
    : sortedOrphans.map((entry) => entry.group);

  const orderedGroups = [...headOrphans, ...sequencedGroups, ...middleOrphans, ...tailOrphans];

  return {
    grouped: Boolean(grouped),
    groups: orderedGroups,
  };
};

const SURVEY_TYPE_OPTIONS = [
  {
    value: "single",
    label: "Simple Survey",
    description:
      "Free-form audio interview. Optionally attach a survey guide document for enumerators.",
  },
  {
    value: "custom",
    label: "Custom Survey",
    description:
      "Build a guided question flow, branching logic, and answer routing for Nexus enumerators.",
  },
];

const getSurveyTypeLabel = (surveyType) =>
  surveyType === "custom" ? "Custom Survey" : "Simple Survey";

const getSurveyGuideBannerMeta = (campaign) => {
  const hasDocument = Boolean(campaign?.surveyQuestionDocument);
  return hasDocument
    ? "Open the builder to review branching flow and update the survey guide attached to this campaign."
    : "Open the builder to define questions, branching flow, and enumerator guidance for this campaign.";
};

const resolveCampaignType = (payload) => {
  const data = payload?.data || payload || {};
  const campaignType = String(data?.campaignType || data?.type || "").toLowerCase();
  const surveyType = String(data?.surveyType || "").toLowerCase();

  if (campaignType) return campaignType;
  if (surveyType === "custom") return "custom_survey";

  return "single_survey";
};

const mapPinIconUrl = (s, isSelected = false) => {
  // Create SVG circle marker with sentiment-based color
  const colors = {
    positive: "#10b981", // rgb(16, 185, 129)
    neutral: "#f59e0b", // rgb(245, 158, 11)
    negative: "#ef4444", // rgb(239, 68, 68)
  };

  const color =
    s === "positive" ? colors.positive : s === "negative" ? colors.negative : colors.neutral;

  const markerSize = isSelected ? 32 : 28;
  const center = markerSize / 2;
  const innerRadius = isSelected ? 10 : 11;
  const outerRadius = isSelected ? 14 : 13;
  const ringStroke = isSelected ? "#ffffff" : color;
  const ringOpacity = isSelected ? 0.95 : 0.3;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}">
      <circle cx="${center}" cy="${center}" r="${outerRadius}" fill="${color}" fill-opacity="0.22" stroke="${ringStroke}" stroke-opacity="${ringOpacity}" stroke-width="${isSelected ? 3 : 4}" />
      <circle cx="${center}" cy="${center}" r="${innerRadius}" fill="${color}" stroke="${isSelected ? "#ffffff" : color}" stroke-width="${isSelected ? 2 : 0}" />
    </svg>`;

  return `data:image/svg+xml;base64,${btoa(svg)}`;
};

const resolveCount = (source, preferredKeys = []) => {
  if (source == null) return 0;
  if (typeof source === "number") return source;
  if (typeof source === "string") {
    const parsed = Number(source);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (Array.isArray(source)) return source.length;

  const keys = [
    ...preferredKeys,
    "totalProcessedResponses",
    "totalProcessed",
    "processedResponses",
    "processed",
    "total",
    "count",
    "totalCount",
    "total_count",
  ];

  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === "number") return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }

  for (const key of ["data", "result", "payload", "meta"]) {
    if (source?.[key] && typeof source[key] === "object") {
      const nestedCount = resolveCount(source[key], keys);
      if (nestedCount > 0) return nestedCount;
    }
  }

  return 0;
};

const resolveDistributionEntries = (source) => {
  if (!source) return [];

  const nestedArray = [
    source?.data,
    source?.result,
    source?.payload,
    source?.distribution,
    source?.items,
    source?.counts,
    source?.ageDistribution,
    source?.sexDistribution,
    source?.sentimentDistribution,
    source?.categoryDistribution,
    source?.employmentSectorDistribution,
    source?.keyConcerns,
  ].find((candidate) => Array.isArray(candidate));

  if (nestedArray) return resolveDistributionEntries(nestedArray);

  const nestedObject = [
    source?.data,
    source?.result,
    source?.payload,
    source?.distribution,
    source?.counts,
  ].find((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate));

  if (nestedObject && nestedObject !== source) return resolveDistributionEntries(nestedObject);

  if (Array.isArray(source)) {
    return source
      .map((item) => {
        const label =
          item?.label ??
          item?.name ??
          item?.key ??
          item?.group ??
          item?.ageBand ??
          item?.age_group ??
          item?.sex ??
          item?.gender ??
          item?.sentiment ??
          item?.type ??
          item?.title;
        const count = Number(item?.count ?? item?.value ?? item?.total ?? 0);
        return { label, count };
      })
      .filter((item) => item.label != null);
  }
  return Object.entries(source)
    .map(([label, count]) => ({
      label,
      count: Number(count ?? 0),
    }))
    .filter((item) => Number.isFinite(item.count));
};

const resolveAgeDistributionEntries = (source) => {
  const entries = resolveDistributionEntries(source);
  const counts = AGE_DISTRIBUTION_BUCKETS.reduce((acc, label) => {
    acc[label] = 0;
    return acc;
  }, {});

  entries.forEach((entry) => {
    const bucket = resolveAgeDistributionBucket(entry?.label);
    const count = Number(entry?.count ?? 0);
    if (!Number.isFinite(count)) return;
    counts[bucket] += count;
  });

  return AGE_DISTRIBUTION_BUCKETS.map((label) => ({ label, count: counts[label] || 0 }));
};

const formatEnumeratorInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean).slice(0, 3);

  if (parts.length === 0) return "EN";

  const lowerJoiners = new Set(["de", "del", "dela", "da", "dos", "du", "la", "le"]);

  return parts
    .map((part, idx) => {
      const ch = part[0] || "";
      if (idx > 0 && lowerJoiners.has(part.toLowerCase())) return ch.toLowerCase();
      return ch.toUpperCase();
    })
    .join("");
};

const resolveEnumeratorPerformanceEntries = (source) => {
  if (!source) return [];

  const rows = Array.isArray(source)
    ? source
    : source?.enumerators || source?.items || source?.data || [];

  return rows
    .map((item, idx) => {
      const name =
        item?.name ||
        item?.enumerator ||
        item?.enumeratorName ||
        item?.userName ||
        `Enumerator ${idx + 1}`;
      const initials = item?.initials || formatEnumeratorInitials(name) || "EN";
      const count = Number(item?.count ?? item?.total ?? item?.processed ?? item?.responses ?? 0);

      return { name, initials, count };
    })
    .filter((row) => row.count >= 0)
    .sort((a, b) => b.count - a.count);
};

const toFilterValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
};

const toParameterFilterValue = (value) => toFilterValue(value) || "Unknown";

const IDENTITY_UNKNOWN_OPTION = "__unknown__";

const AGE_BAND_LABELS = {
  under_18: "17 and below",
  "18_29": "18-29",
  "30_44": "30-44",
  "45_59": "45-59",
  "60_plus": "60+",
};

const AGE_BAND_OPTIONS = [
  { value: "under_18", label: "17 and below" },
  { value: "18_29", label: "18-29" },
  { value: "30_44", label: "30-44" },
  { value: "45_59", label: "45-59" },
  { value: "60_plus", label: "60+" },
  { value: null, label: "Prefer not to say" },
];

const SEX_OPTIONS = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: null, label: "Prefer not to say" },
];

const hasOwn = (source, key) => Object.prototype.hasOwnProperty.call(source || {}, key);

const readOwnField = (source, keys) => {
  for (const key of keys) {
    if (hasOwn(source, key)) return source[key];
  }
  return undefined;
};

const readNestedOwnField = (source, paths) => {
  for (const path of paths) {
    let cursor = source;
    let valid = true;

    for (const key of path) {
      if (!hasOwn(cursor, key)) {
        valid = false;
        break;
      }
      cursor = cursor[key];
    }

    if (valid) return cursor;
  }

  return undefined;
};

const normalizeAnswerLookupKey = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const getStringAnswerValue = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (typeof value === "object") {
    const candidate =
      value?.value ??
      value?.answer ??
      value?.response ??
      value?.text ??
      value?.label ??
      value?.selectedOption ??
      value?.selectedValue;
    return candidate == null ? "" : String(candidate).trim();
  }
  return String(value).trim();
};

const getStandardDemographicAnswers = (item) => {
  const groupedAnswers = readOwnField(item, [
    "standardAnswers",
    "standard_answers",
    "preliminaryAnswers",
    "preliminary_answers",
    "demographicAnswers",
    "demographic_answers",
  ]);

  const normalizedMap = {};

  const appendEntry = (key, value) => {
    const normalizedKey = normalizeAnswerLookupKey(key);
    if (!normalizedKey || Object.prototype.hasOwnProperty.call(normalizedMap, normalizedKey)) {
      return;
    }
    const normalizedValue = getStringAnswerValue(value);
    if (!normalizedValue) return;
    normalizedMap[normalizedKey] = normalizedValue;
  };

  if (Array.isArray(groupedAnswers)) {
    groupedAnswers.forEach((entry) => {
      appendEntry(entry?.key || entry?.questionKey || entry?.question || entry?.label, entry);
    });
  } else if (groupedAnswers && typeof groupedAnswers === "object") {
    Object.entries(groupedAnswers).forEach(([key, value]) => appendEntry(key, value));
  }

  const flatAnswerSources = [
    readOwnField(item, ["answers", "responseAnswers", "surveyAnswers", "questionAnswers"]),
    readNestedOwnField(item, [
      ["survey", "answers"],
      ["responses", "answers"],
    ]),
  ];

  flatAnswerSources.forEach((source) => {
    if (Array.isArray(source)) {
      source.forEach((entry) => {
        appendEntry(entry?.key || entry?.questionKey || entry?.question || entry?.label, entry);
      });
      return;
    }

    if (source && typeof source === "object") {
      Object.entries(source).forEach(([key, value]) => appendEntry(key, value));
    }
  });

  const resolveByAliases = (aliases) => {
    for (const alias of aliases) {
      const key = normalizeAnswerLookupKey(alias);
      if (Object.prototype.hasOwnProperty.call(normalizedMap, key)) {
        return normalizedMap[key];
      }
    }

    return "";
  };

  const gender = resolveByAliases([
    "gendercheck",
    "gender",
    "genderofrespondent",
    "respondentgender",
    "sex",
  ]);
  const age = resolveByAliases([
    "agecheck",
    "age",
    "respondentage",
    "ilangtaonnapokayo",
    "agegroup",
    "ageband",
    "agebracket",
  ]);

  return {
    sex: gender || undefined,
    age: age || undefined,
  };
};

const normalizeSexValue = (value) => {
  if (value === null) return null;
  if (value === undefined) return undefined;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "male") return "male";
  if (normalized === "female") return "female";
  if (
    normalized === "unknown" ||
    normalized === "prefer not to say" ||
    normalized === "prefer_not_to_say" ||
    normalized === "n/a" ||
    normalized === "na" ||
    normalized === "null"
  ) {
    return null;
  }

  return undefined;
};

const normalizeAgeBandValue = (value) => {
  if (value === null) return null;
  if (value === undefined) return undefined;

  const normalized = String(value).trim().toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");

  if (!normalized) return undefined;
  if (normalized === "under_18" || normalized === "0_17" || normalized === "17_and_below") {
    return "under_18";
  }
  if (normalized === "18_29") return "18_29";
  if (normalized === "30_44" || normalized === "35_44") return "30_44";
  if (normalized === "45_59" || normalized === "45_54" || normalized === "55_64") {
    return "45_59";
  }
  if (normalized === "60_plus" || normalized === "60") return "60_plus";
  if (
    normalized === "unknown" ||
    normalized === "prefer_not_to_say" ||
    normalized === "n_a" ||
    normalized === "na" ||
    normalized === "null"
  ) {
    return null;
  }

  return undefined;
};

const formatSexLabel = (value, unknownLabel = "Unknown") => {
  const normalized = normalizeSexValue(value);
  if (normalized === "male") return "Male";
  if (normalized === "female") return "Female";
  if (normalized === null) return unknownLabel;

  const fallback = String(value ?? "").trim();
  return fallback || unknownLabel;
};

const formatAgeBandLabel = (value, unknownLabel = "Unknown") => {
  const normalized = normalizeAgeBandValue(value);
  if (normalized === null) return unknownLabel;
  if (normalized && AGE_BAND_LABELS[normalized]) return AGE_BAND_LABELS[normalized];

  const fallback = String(value ?? "").trim();
  if (!fallback) return unknownLabel;

  return fallback
    .replace(/_/g, "-")
    .replace(/plus$/i, "+")
    .replace(/\bunder\b/i, "17 and below");
};

const normalizeIdentityFieldsFromApi = (item) => {
  const standardAnswers = getStandardDemographicAnswers(item);

  const sexValue = readOwnField(item, ["sex", "gender", "respondentSex"]);
  const ageBandValue = readOwnField(item, ["ageBand", "age_band"]);
  const ageGroupValue = readOwnField(item, ["ageGroup", "age_group", "ageBracket", "age_bracket"]);

  const normalizedSex = normalizeSexValue(standardAnswers.sex ?? sexValue);
  const normalizedAgeBand = normalizeAgeBandValue(
    standardAnswers.age ?? ageBandValue ?? ageGroupValue,
  );
  const rawAgeGroup = String(ageGroupValue ?? "").trim();
  const standardAgeGroup = String(standardAnswers.age ?? "").trim();

  return {
    sex: normalizedSex === undefined ? (standardAnswers.sex ?? sexValue) : normalizedSex,
    ageBand: normalizedAgeBand,
    ageGroup:
      normalizedAgeBand !== undefined
        ? formatAgeBandLabel(normalizedAgeBand)
        : standardAgeGroup || rawAgeGroup || formatAgeBandLabel(ageBandValue ?? null),
    originalSex: readOwnField(item, ["originalSex", "original_sex"]),
    sexEdited: Boolean(readOwnField(item, ["sexEdited", "sex_edited"])),
    sexEditedAt: readOwnField(item, ["sexEditedAt", "sex_edited_at"]),
    sexEditedBy: readOwnField(item, ["sexEditedBy", "sex_edited_by"]),
    originalAgeBand: readOwnField(item, ["originalAgeBand", "original_age_band"]),
    ageBandEdited: Boolean(readOwnField(item, ["ageBandEdited", "age_band_edited"])),
    ageBandEditedAt: readOwnField(item, ["ageBandEditedAt", "age_band_edited_at"]),
    ageBandEditedBy: readOwnField(item, ["ageBandEditedBy", "age_band_edited_by"]),
  };
};

const AGE_GENDER_BANDS = [
  { key: "0-17", label: "17 and below" },
  { key: "18-29", label: "18-29" },
  { key: "30-44", label: "30-44" },
  { key: "45-59", label: "45-59" },
  { key: "60+", label: "60+" },
];

const resolveAgeGenderMixFromAnalytics = ({ ageSource, sexSource, campaignTotal }) => {
  const rows = AGE_GENDER_BANDS.map((band) => ({ ...band, male: 0, female: 0 }));
  const requestedTotal = Number(campaignTotal);
  const ageEntries = resolveAgeDistributionEntries(ageSource).filter((entry) => entry.count > 0);
  const sexEntries = resolveDistributionEntries(sexSource);
  const male = sexEntries.reduce((sum, entry) => {
    const sex = normalizeSexValue(entry?.label);
    return sex === "male" ? sum + Number(entry.count || 0) : sum;
  }, 0);
  const female = sexEntries.reduce((sum, entry) => {
    const sex = normalizeSexValue(entry?.label);
    return sex === "female" ? sum + Number(entry.count || 0) : sum;
  }, 0);
  const ageTotal = ageEntries.reduce((sum, entry) => sum + Number(entry.count || 0), 0);
  const total = Math.max(
    Number.isFinite(requestedTotal) ? requestedTotal : 0,
    ageTotal,
    male + female,
  );

  ageEntries.forEach((entry) => {
    const row = rows.find((item) => item.key === entry.label);
    if (!row) return;
    row.male = Number(entry.count || 0);
  });

  return {
    rows,
    total,
    male,
    female,
    unclassified: Math.max(total - ageTotal, 0),
    maxCount: Math.max(...rows.flatMap((row) => [row.male, row.female]), 1),
  };
};

const doesRecordingMatchApiItem = (recording, item) => {
  if (!recording || !item) return false;

  const recordingIds = [
    recording?.fieldResponseProcessedId,
    recording?.fieldResponseId,
    recording?.id,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  const itemIds = [
    item?.fieldResponseProcessedId,
    item?.field_response_processed_id,
    item?.fieldResponseId,
    item?.field_response_id,
    item?.id,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return recordingIds.some((value) => itemIds.includes(value));
};

const RECORDING_FILTER_DEFS = [
  {
    key: "sentiment",
    title: "Sentiment",
    responseKey: "sentiments",
    sortOrder: ["negative", "neutral", "positive", "mixed"],
    getValue: (rec) => toFilterValue(String(rec?.sentiment || "").toLowerCase()),
    formatLabel: (value) => value.charAt(0).toUpperCase() + value.slice(1),
  },
  {
    key: "enumerator",
    title: "Enumerator",
    responseKey: "enumerators",
    getValue: (rec) => toParameterFilterValue(rec?.name || rec?.enumerator),
  },
  {
    key: "sex",
    title: "Gender",
    responseKey: "sexes",
    getValue: (rec) => toParameterFilterValue(rec?.sex),
    formatLabel: (value) => formatSexLabel(value),
  },
  {
    key: "ageBand",
    title: "Age Group",
    responseKey: "ageBands",
    getValue: (rec) => toParameterFilterValue(rec?.ageBand || rec?.ageGroup),
    formatLabel: (value) => formatAgeBandLabel(value),
  },
  {
    key: "cohortIds",
    title: "Cohorts",
    responseKey: "cohortGroups",
    getValue: (rec) => toParameterFilterValue(rec?.cohortId),
  },
];

const getFilterOptionValue = (item, key) => {
  if (key === "cohortIds") {
    return toParameterFilterValue(item?.cohortId ?? item?.id ?? item?.value);
  }

  return toParameterFilterValue(item?.value ?? item?.name ?? item?.label ?? item?.key);
};

const getFilterOptionLabel = (item, value, def) => {
  const label = String(item?.label ?? item?.name ?? item?.value ?? value ?? "").trim();
  return label || (def.formatLabel ? def.formatLabel(value) : value);
};

const getFilterOptionCount = (item) => {
  const count = Number(item?.count ?? item?.total ?? item?.records ?? 0);
  return Number.isFinite(count) ? count : 0;
};

const normalizeRecordingFilterOption = (item, def, groupLabel = "") => {
  const value = getFilterOptionValue(item, def.key);
  if (!value) return null;

  const label = getFilterOptionLabel(item, value, def);
  return {
    value,
    label: groupLabel ? `${groupLabel}: ${label}` : label,
    count: getFilterOptionCount(item),
  };
};

const normalizeRecordingFilterOptions = (payload) => {
  const source = payload?.data || payload || {};

  return RECORDING_FILTER_DEFS.reduce((acc, def) => {
    if (def.key === "cohortIds") {
      acc[def.key] = (Array.isArray(source.cohortGroups) ? source.cohortGroups : [])
        .flatMap((group) => {
          const groupLabel = String(group?.label ?? group?.name ?? group?.value ?? "").trim();
          return (Array.isArray(group?.cohorts) ? group.cohorts : []).map((cohort) =>
            normalizeRecordingFilterOption(cohort, def, groupLabel),
          );
        })
        .filter(Boolean);
      return acc;
    }

    acc[def.key] = (Array.isArray(source[def.responseKey]) ? source[def.responseKey] : [])
      .map((item) => normalizeRecordingFilterOption(item, def))
      .filter(Boolean);
    return acc;
  }, {});
};

const getRecordingListFilterParams = (filters) => ({
  sentiment: filters?.sentiment || [],
  enumerator: filters?.enumerator || [],
  sex: filters?.sex || [],
  ageBand: filters?.ageBand || [],
  cohortIds: filters?.cohortIds || [],
});

const resolveProcessedResponsePagination = (processed) => {
  const source = processed?.pagination || processed?.meta || processed || {};
  const totalPages = Number(source?.totalPages ?? source?.total_pages ?? 0);

  return {
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : null,
    hasNextPage: Boolean(source?.hasNextPage ?? source?.has_next_page),
  };
};

const createRecordingFilterState = () =>
  RECORDING_FILTER_DEFS.reduce((acc, def) => {
    acc[def.key] = [];
    return acc;
  }, {});

const cloneRecordingFilterState = (state) =>
  RECORDING_FILTER_DEFS.reduce((acc, def) => {
    acc[def.key] = Array.isArray(state?.[def.key]) ? [...state[def.key]] : [];
    return acc;
  }, {});

const getMaxEntryCount = (entries) => Math.max(...entries.map((e) => Number(e?.count || 0)), 1);

const parseEnumeratorCount = (value) => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

const formatRecordIdValue = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";

  const digitsOnly = (raw.match(/\d+/g) || []).join("");
  if (!digitsOnly) return raw;
  return digitsOnly.length < 6 ? digitsOnly.padStart(6, "0") : digitsOnly;
};

const formatRecordIdLabel = (value) => {
  return `ID# ${formatRecordIdValue(value)}`;
};

const normalizeSentimentLabel = (value) => {
  const raw = String(value || "")
    .trim()
    .toLowerCase();
  if (!raw) return null;
  if (raw.includes("positive") || raw === "pos") return "positive";
  if (raw.includes("negative") || raw === "neg") return "negative";
  if (raw.includes("neutral") || raw.includes("mixed")) return "neutral";
  return null;
};

const resolveSentimentBreakdown = (sentimentSource) => {
  const counts = {
    positive: 0,
    negative: 0,
    neutral: 0,
  };

  const sentimentCandidates = [
    sentimentSource,
    sentimentSource?.data,
    sentimentSource?.distribution,
    sentimentSource?.sentiment,
    sentimentSource?.counts,
    sentimentSource?.result,
  ].filter(Boolean);

  sentimentCandidates.forEach((candidate) => {
    const entries = resolveDistributionEntries(candidate);
    entries.forEach((entry) => {
      const key = normalizeSentimentLabel(entry?.label);
      const value = Number(entry?.count || 0);
      if (!key || !Number.isFinite(value)) return;
      counts[key] += value;
    });
  });

  return counts;
};

const getWelcomeChatMessage = (campaignCode) => ({
  id: `ai-welcome-${campaignCode}`,
  role: "assistant",
  text: `Hi! I am your campaign assistant for ${campaignCode}. Ask me for quick insights, summaries, or suggested actions.`,
});

const getMapScopeAnswer = ({ prompt = "", respondents = [], campaignCode = "" }) => {
  if (!respondents.length) {
    return "No respondents are currently in view. Pan or zoom out to widen the viewport and ask again.";
  }

  const positive = respondents.filter((rec) => rec.sentiment === "positive").length;
  const neutral = respondents.filter((rec) => rec.sentiment === "neutral").length;
  const negative = respondents.filter((rec) => rec.sentiment === "negative").length;
  const concernCounts = new Map();
  respondents.forEach((rec) => {
    (rec.concerns || []).forEach((concern) => {
      const key = String(concern || "").trim();
      if (!key) return;
      concernCounts.set(key, (concernCounts.get(key) || 0) + 1);
    });
  });
  const topConcerns = Array.from(concernCounts.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([key]) => key);

  const summary = [
    `${respondents.length} respondent${respondents.length === 1 ? "" : "s"} are currently in view`,
    `${positive} positive, ${neutral} neutral/mixed, ${negative} negative`,
  ];

  if (topConcerns.length) {
    summary.push(`top concerns: ${topConcerns.join(", ")}`);
  }

  const promptText = String(prompt || "").trim();
  if (!promptText) {
    return `${summary.join("; ")}.`;
  }

  return `${summary.join("; ")}. Based on the current viewport for ${campaignCode || "this campaign"}, ${promptText.toLowerCase().startsWith("what") ? "the visible respondents suggest" : "this view suggests"} a ${positive >= negative ? "more favorable" : "more cautious"} sentiment profile in this area.`;
};

const mapApiChatMessage = (message) => ({
  id: `chat-msg-${message?.insightChatMessageId ?? Date.now()}`,
  role: message?.role === "user" ? "user" : "assistant",
  text: message?.content || "",
});

const sortThreadsByRecent = (threads = []) => {
  if (!Array.isArray(threads)) return [];

  return [...threads].sort((a, b) => {
    const aTime = new Date(a?.lastMessageAt || a?.updatedAt || a?.createdAt || 0).getTime();
    const bTime = new Date(b?.lastMessageAt || b?.updatedAt || b?.createdAt || 0).getTime();
    return bTime - aTime;
  });
};

const formatThreadDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
};

const getThreadLabel = (thread = {}, index = 0) => {
  const fromApi =
    thread?.title ||
    thread?.name ||
    thread?.subject ||
    thread?.lastMessagePreview ||
    thread?.lastPrompt ||
    thread?.prompt;

  if (fromApi && String(fromApi).trim()) {
    return String(fromApi).trim();
  }

  return `Conversation ${index + 1}`;
};

const mapChatThread = (thread = {}, index = 0) => ({
  ...thread,
  insightChatThreadId: thread?.insightChatThreadId,
  displayLabel: getThreadLabel(thread, index),
  displayTime: formatThreadDate(thread?.lastMessageAt || thread?.updatedAt || thread?.createdAt),
});

const formatInlineCode = (text = "") => {
  const parts = String(text).split(/(`[^`]+`)/g);
  return parts
    .map((part) => {
      if (/^`[^`]+`$/.test(part)) {
        const codeContent = part.slice(1, -1);
        return `<code>${sanitizeHtml(codeContent).replace(/<br>/g, "")}</code>`;
      }
      return sanitizeHtml(part);
    })
    .join("");
};

const formatChatMessageHtml = (text = "") => {
  const source = String(text || "").replace(/\r\n/g, "\n");
  const lines = source.split("\n");
  const blocks = [];

  let paragraphBuffer = [];
  let listBuffer = [];
  let codeBuffer = [];
  let inCodeBlock = false;

  const flushParagraph = () => {
    if (!paragraphBuffer.length) return;
    const paragraph = paragraphBuffer.join(" ").trim();
    if (paragraph) {
      blocks.push(`<p>${formatInlineCode(paragraph)}</p>`);
    }
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (!listBuffer.length) return;
    const items = listBuffer.map((item) => `<li>${formatInlineCode(item)}</li>`).join("");
    blocks.push(`<ul>${items}</ul>`);
    listBuffer = [];
  };

  const flushCode = () => {
    if (!codeBuffer.length) return;
    const codeText = codeBuffer.join("\n");
    blocks.push(`<pre><code>${sanitizeHtml(codeText).replace(/<br>/g, "\n")}</code></pre>`);
    codeBuffer = [];
  };

  lines.forEach((line) => {
    if (line.trim().startsWith("```")) {
      flushParagraph();
      flushList();

      if (inCodeBlock) {
        flushCode();
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBuffer.push(line);
      return;
    }

    if (!line.trim()) {
      flushParagraph();
      flushList();
      return;
    }

    const listMatch = line.match(/^\s*(?:[-*]|\d+\.)\s+(.+)$/);
    if (listMatch) {
      flushParagraph();
      listBuffer.push(listMatch[1]);
      return;
    }

    flushList();
    paragraphBuffer.push(line.trim());
  });

  flushParagraph();
  flushList();
  flushCode();

  return blocks.join("");
};

const resolveLatestThread = (threads = []) => {
  if (!Array.isArray(threads) || threads.length === 0) return null;

  return sortThreadsByRecent(threads)[0];
};

const escapeRegExp = (value = "") => String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const hasMapMention = (text = "") => {
  const normalized = String(text || "");
  return /\bmap\b|\bmap\s*view\b|\bmapped\b|\bgeospatial\b|\bgeo\s*view\b|\bpin(s)?\b/i.test(
    normalized,
  );
};

const extractInsightFiltersFromText = (text = "", recordings = []) => {
  const emptyState = createRecordingFilterState();
  const normalized = String(text || "").toLowerCase();

  if (!normalized.trim() || !Array.isArray(recordings) || recordings.length === 0) {
    return emptyState;
  }

  const nextState = createRecordingFilterState();

  RECORDING_FILTER_DEFS.forEach((def) => {
    const values = Array.from(
      new Set(
        recordings
          .map((recording) => def.getValue(recording))
          .filter((value) => value != null)
          .map((value) => String(value).toLowerCase()),
      ),
    );

    if (!values.length) return;

    const matchedValues = values.filter((value) => {
      const spaced = value.replace(/[_-]+/g, " ").trim();
      const candidates = new Set([value, spaced]);

      if (def.key === "sent") {
        if (value === "negative") {
          candidates.add("negative sentiment");
          candidates.add("negative sentiments");
        }
        if (value === "positive") {
          candidates.add("positive sentiment");
          candidates.add("positive sentiments");
        }
        if (value === "neutral") {
          candidates.add("neutral sentiment");
          candidates.add("mixed");
          candidates.add("mixed or neutral");
        }
      }

      return Array.from(candidates).some((candidate) => {
        if (!candidate) return false;
        const expr = new RegExp(`(^|\\W)${escapeRegExp(candidate)}(\\W|$)`, "i");
        return expr.test(normalized);
      });
    });

    if (matchedValues.length > 0) {
      nextState[def.key] = matchedValues;
    }
  });

  return nextState;
};

const TABS = [
  {
    id: "info",
    label: "Campaign Info",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
  {
    id: "exec",
    label: "Executive Summary",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    ),
  },
  {
    id: "map",
    label: "Map View",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
        />
      </svg>
    ),
  },
  {
    id: "recs",
    label: "Recordings",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
        />
      </svg>
    ),
  },
  {
    id: "chat",
    label: "AI Chat",
    icon: (
      <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"
        />
      </svg>
    ),
  },
];

const CampaignsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { campaignCode: campaignCodeParam } = useParams();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsError, setCampaignsError] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [createCampaignError, setCreateCampaignError] = useState(null);
  const [isEndCampaignModalOpen, setIsEndCampaignModalOpen] = useState(false);
  const [isEndingCampaign, setIsEndingCampaign] = useState(false);
  const [endCampaignError, setEndCampaignError] = useState(null);
  const [campaignToEnd, setCampaignToEnd] = useState(null);
  const [campaignForm, setCampaignForm] = useState({
    code: "",
    name: "",
    date: "",
    location: "",
    provinceCode: "",
    targetCode: "",
    surveyType: "custom",
    surveyDocument: null,
  });
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [municipalityOptions, setMunicipalityOptions] = useState([]);
  const [provinceFieldError, setProvinceFieldError] = useState("");
  const [municipalityFieldError, setMunicipalityFieldError] = useState("");
  const provinceFieldRef = useRef(null);
  const municipalityFieldRef = useRef(null);
  const [mapChatThreads, setMapChatThreads] = useState([]);
  const [mapChatThreadId, setMapChatThreadId] = useState(null);
  const [mapChatMessages, setMapChatMessages] = useState([]);
  const [mapChatInput, setMapChatInput] = useState("");
  const [mapChatTyping, setMapChatTyping] = useState(false);
  const [mapScopeStats, setMapScopeStats] = useState({ visible: 0, total: 0, zoom: 10 });
  const mapChatLogRef = useRef(null);
  const [openedCampaign, setOpenedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState("info");
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [processedResponses, setProcessedResponses] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatTyping, setChatTyping] = useState(false);
  const [chatThreads, setChatThreads] = useState([]);
  const [chatThreadId, setChatThreadId] = useState(null);
  const [chatInitializing, setChatInitializing] = useState(false);
  const [recordingsPage, setRecordingsPage] = useState(1);
  const [isRecordingsPageLoading, setIsRecordingsPageLoading] = useState(false);
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [campaignRecordingTotals, setCampaignRecordingTotals] = useState({});
  const [campaignListDemographics, setCampaignListDemographics] = useState({});
  const [campaignListEnumeratorCounts, setCampaignListEnumeratorCounts] = useState({});
  const chatInitSequenceRef = useRef(0);
  const audioRef = useRef(null);
  const listAudioRef = useRef(null);
  const groupAudioRefs = useRef({});
  const mapRef = useRef(null);
  const mapCenterRef = useRef({ lat: 14.2167, lng: 121.1653 });
  const [selectedMapPin, setSelectedMapPin] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioCurrent, setAudioCurrent] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [inlineAudioRecordId, setInlineAudioRecordId] = useState(null);
  const [inlineAudioPlaying, setInlineAudioPlaying] = useState(false);
  const [inlineAudioCurrent, setInlineAudioCurrent] = useState(0);
  const [inlineAudioDuration, setInlineAudioDuration] = useState(0);
  const [activeGroupAudioId, setActiveGroupAudioId] = useState(null);
  const [groupAudioPlaying, setGroupAudioPlaying] = useState(false);
  const [groupAudioCurrent, setGroupAudioCurrent] = useState(0);
  const [, setDownloadingRecordingId] = useState(null);
  const [isRecsFilterOpen, setIsRecsFilterOpen] = useState(false);
  const [recsFiltersApplied, setRecsFiltersApplied] = useState(() => createRecordingFilterState());
  const [recsFiltersDraft, setRecsFiltersDraft] = useState(() => createRecordingFilterState());
  const [recIdSearch, setRecIdSearch] = useState("");
  const [recordingsFilterOptions, setRecordingsFilterOptions] = useState(null);
  const [recordingToDelete, setRecordingToDelete] = useState(null);
  const [isDeleteRecordingConfirmOpen, setIsDeleteRecordingConfirmOpen] = useState(false);
  const [isDeleteRecordingSuccessOpen, setIsDeleteRecordingSuccessOpen] = useState(false);
  const [isDeletingRecording, setIsDeletingRecording] = useState(false);
  const [deleteRecordingError, setDeleteRecordingError] = useState(null);
  const [campaignQuestionsData, setCampaignQuestionsData] = useState(null);
  const [, setCampaignQuestionsLoading] = useState(false);
  const [campaignType, setCampaignType] = useState(null);
  const [campaignTypeLoading, setCampaignTypeLoading] = useState(false);
  const [transcriptLines, setTranscriptLines] = useState([]);
  const [transcriptGroups, setTranscriptGroups] = useState([]);
  const [isTranscriptGrouped, setIsTranscriptGrouped] = useState(false);
  const [transcriptHistory, setTranscriptHistory] = useState([]);
  const [transcriptEditingIndex, setTranscriptEditingIndex] = useState(null);
  const [transcriptDraftLine, setTranscriptDraftLine] = useState("");
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState(null);
  const [showRestoreTranscriptConfirm, setShowRestoreTranscriptConfirm] = useState(false);
  const [transcriptToast, setTranscriptToast] = useState(null);
  const [transcriptSaveRowIndex, setTranscriptSaveRowIndex] = useState(null);
  const [transcriptRestoreRowIndex, setTranscriptRestoreRowIndex] = useState(null);
  const [isTranscriptRestoreAllLoading, setIsTranscriptRestoreAllLoading] = useState(false);
  const [identitySavingField, setIdentitySavingField] = useState(null);
  const [identityError, setIdentityError] = useState(null);

  useEffect(() => {
    if (!openedCampaign?.code || activeTab !== "recs") return undefined;

    let isMounted = true;
    setIsRecordingsPageLoading(true);

    getFieldResponsesProcessed({
      campaignCode: openedCampaign.code,
      page: recordingsPage,
      perPage: RECORDINGS_API_PER_PAGE,
      ...getRecordingListFilterParams(recsFiltersApplied),
      recordingId: recIdSearch,
    })
      .then((processed) => {
        if (!isMounted) return;

        setProcessedResponses(processed);
      })
      .catch(() => {
        if (!isMounted) return;
        setAnalyticsError("Failed to load analytics data.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsRecordingsPageLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [activeTab, openedCampaign?.code, recIdSearch, recordingsPage, recsFiltersApplied]);

  useEffect(() => {
    if (!openedCampaign?.code || activeTab !== "recs") return undefined;
    if (recordingsFilterOptions) return undefined;

    let isMounted = true;

    getFieldResponseProcessedFilterOptions({ campaignCode: openedCampaign.code })
      .then((options) => {
        if (!isMounted) return;
        setRecordingsFilterOptions(normalizeRecordingFilterOptions(options));
      })
      .catch(() => {
        if (!isMounted) return;
        setRecordingsFilterOptions(normalizeRecordingFilterOptions({}));
      });

    return () => {
      isMounted = false;
    };
  }, [activeTab, openedCampaign?.code, recordingsFilterOptions]);

  useEffect(() => {
    let isMounted = true;

    setCampaignsLoading(true);
    setCampaignsError(null);

    getCampaignsRequest()
      .then((data) => {
        if (!isMounted) return;
        const rows = Array.isArray(data?.campaigns)
          ? data.campaigns
          : Array.isArray(data)
            ? data
            : [];
        setCampaigns(rows.map((campaign) => normalizeCampaign(campaign)));
      })
      .catch(() => {
        if (!isMounted) return;
        setCampaigns([]);
        setCampaignsError("Failed to load campaigns.");
      })
      .finally(() => {
        if (!isMounted) return;
        setCampaignsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (!campaigns.length) {
      setCampaignRecordingTotals({});
      setCampaignListDemographics({});
      setCampaignListEnumeratorCounts({});
      return undefined;
    }

    Promise.all(
      campaigns.map(async (campaign) => {
        try {
          const [total, sex, age, enumeratorPerf] = await Promise.all([
            getTotalProcessedResponses({ campaignCode: campaign.code }),
            getSexDistribution({ campaignCode: campaign.code }).catch(() => null),
            getAgeDistribution({ campaignCode: campaign.code }).catch(() => null),
            getEnumeratorPerformance({ campaignCode: campaign.code }).catch(() => null),
          ]);
          const enumeratorEntries = resolveEnumeratorPerformanceEntries(enumeratorPerf);
          const processedTotal = resolveCount(total, ["totalProcessedResponses"]);
          return [
            campaign.code,
            {
              total: processedTotal,
              sex,
              age,
              enumeratorCount: enumeratorEntries.length,
            },
          ];
        } catch {
          return [campaign.code, { total: 0, sex: null, age: null, enumeratorCount: 0 }];
        }
      }),
    ).then((entries) => {
      if (!isMounted) return;
      const totals = {};
      const demog = {};
      const enumCounts = {};
      entries.forEach(([code, data]) => {
        totals[code] = data.total;
        demog[code] = { sex: data.sex, age: data.age };
        enumCounts[code] = data.enumeratorCount;
      });
      setCampaignRecordingTotals(totals);
      setCampaignListDemographics(demog);
      setCampaignListEnumeratorCounts(enumCounts);
    });

    return () => {
      isMounted = false;
    };
  }, [campaigns]);

  useEffect(() => {
    if (!selectedRecording) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setAudioPlaying(false);
      setAudioCurrent(0);
      setAudioDuration(0);
      setTranscriptLines([]);
      setTranscriptGroups([]);
      setIsTranscriptGrouped(false);
      setTranscriptHistory([]);
      setTranscriptEditingIndex(null);
      setTranscriptDraftLine("");
      setTranscriptError(null);
      setTranscriptLoading(false);
      setShowRestoreTranscriptConfirm(false);
      setTranscriptToast(null);
      setTranscriptSaveRowIndex(null);
      setTranscriptRestoreRowIndex(null);
      setIsTranscriptRestoreAllLoading(false);
      setIdentitySavingField(null);
      setIdentityError(null);
      Object.values(groupAudioRefs.current || {}).forEach((player) => {
        if (!player) return;
        player.pause();
      });
      setActiveGroupAudioId(null);
      setGroupAudioPlaying(false);
      setGroupAudioCurrent(0);
      return;
    }

    if (listAudioRef.current) {
      listAudioRef.current.pause();
    }
    Object.values(groupAudioRefs.current || {}).forEach((player) => {
      if (!player) return;
      player.pause();
    });
    setInlineAudioPlaying(false);
    setActiveGroupAudioId(null);
    setGroupAudioPlaying(false);
    setGroupAudioCurrent(0);
    setIdentitySavingField(null);
    setIdentityError(null);
  }, [selectedRecording]);

  useEffect(() => {
    if (!selectedRecording) return undefined;

    const fallbackLines = normalizeTranscriptLines(null, selectedRecording.transcript || "");
    const fallbackGroups = { grouped: false, groups: [] };
    const processedId =
      selectedRecording.fieldResponseProcessedId ||
      selectedRecording.id ||
      selectedRecording.fieldResponseId;

    let isMounted = true;

    setTranscriptLoading(true);
    setTranscriptError(null);
    setTranscriptHistory([]);
    setTranscriptEditingIndex(null);
    setTranscriptDraftLine("");

    if (!processedId) {
      setTranscriptLines(cloneTranscriptLines(fallbackLines));
      setTranscriptGroups(fallbackGroups.groups);
      setIsTranscriptGrouped(fallbackGroups.grouped);
      setTranscriptLoading(false);
      return undefined;
    }

    getFieldResponseProcessedTranscript({ fieldResponseProcessedId: processedId })
      .then((payload) => {
        if (!isMounted) return;
        const lines = normalizeTranscriptLines(payload, selectedRecording.transcript || "");
        const grouping = normalizeTranscriptGroups(payload);
        setTranscriptLines(cloneTranscriptLines(lines));
        setTranscriptGroups(grouping.groups);
        setIsTranscriptGrouped(grouping.grouped);
      })
      .catch(() => {
        if (!isMounted) return;
        setTranscriptLines(cloneTranscriptLines(fallbackLines));
        setTranscriptGroups(fallbackGroups.groups);
        setIsTranscriptGrouped(fallbackGroups.grouped);
        setTranscriptError("Failed to load transcript from API. Showing available transcript.");
      })
      .finally(() => {
        if (!isMounted) return;
        setTranscriptLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedRecording]);

  const formatAudioTime = (secs) => {
    if (!secs || isNaN(secs)) return "0:00";
    const m = Math.floor(secs / 60);
    const s = String(Math.floor(secs % 60)).padStart(2, "0");
    return `${m}:${s}`;
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (audioPlaying) {
      el.pause();
    } else {
      el.play();
    }
  };

  const seekAudio = (e) => {
    const el = audioRef.current;
    if (!el || !audioDuration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * audioDuration;
  };

  const toggleInlinePlay = (rec) => {
    const el = listAudioRef.current;
    if (!el || !rec?.audioUrl) return;

    if (inlineAudioRecordId !== rec.id) {
      setInlineAudioRecordId(rec.id);
      setInlineAudioCurrent(0);
      setInlineAudioDuration(0);
      el.src = rec.audioUrl;
      el.load();
      el.play();
      return;
    }

    if (inlineAudioPlaying) {
      el.pause();
    } else {
      el.play();
    }
  };

  const stopInlinePlay = () => {
    const el = listAudioRef.current;
    if (!el || inlineAudioRecordId == null) return;
    el.pause();
    el.currentTime = 0;
    setInlineAudioPlaying(false);
    setInlineAudioCurrent(0);
  };

  const seekInlineAudio = (event, recId) => {
    if (inlineAudioRecordId !== recId || !inlineAudioDuration) return;
    const el = listAudioRef.current;
    if (!el) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    el.currentTime = ratio * inlineAudioDuration;
  };

  useEffect(() => {
    if (!selectedRecording) return undefined;
    const onEsc = (e) => {
      if (e.key !== "Escape") return;

      const activeTagName = document?.activeElement?.tagName;
      if (activeTagName === "SELECT") {
        return;
      }

      if (showRestoreTranscriptConfirm) {
        setShowRestoreTranscriptConfirm(false);
        return;
      }

      if (transcriptEditingIndex != null) {
        e.preventDefault();
        setTranscriptEditingIndex(null);
        setTranscriptDraftLine("");
        return;
      }

      setSelectedRecording(null);
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [selectedRecording, showRestoreTranscriptConfirm, transcriptEditingIndex]);

  useEffect(() => {
    if (!transcriptToast) return undefined;
    const timer = window.setTimeout(() => {
      setTranscriptToast(null);
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [transcriptToast]);

  const updateSelectedRecordingTranscript = (lines) => {
    // Keep edits local to the opened recording modal for now.
    serializeTranscriptLines(lines);
  };

  const beginTranscriptEdit = (segmentIndex) => {
    const line = transcriptLines.find((entry) => entry.segmentIndex === segmentIndex);
    if (!line) return;
    setTranscriptEditingIndex(segmentIndex);
    setTranscriptDraftLine(line.text);
  };

  const cancelTranscriptEdit = () => {
    setTranscriptEditingIndex(null);
    setTranscriptDraftLine("");
  };

  const saveTranscriptLine = async (segmentIndex) => {
    const line = transcriptLines.find((entry) => entry.segmentIndex === segmentIndex);
    if (!line) return;

    const nextText = String(transcriptDraftLine || "").trim();
    if (!nextText || nextText === line.text) {
      cancelTranscriptEdit();
      return;
    }

    const processedId =
      selectedRecording?.fieldResponseProcessedId ||
      selectedRecording?.id ||
      selectedRecording?.fieldResponseId;
    if (!processedId) {
      setTranscriptError("Cannot save transcript row: missing recording id.");
      return;
    }

    const snapshot = cloneTranscriptLines(transcriptLines);
    setTranscriptSaveRowIndex(segmentIndex);
    setTranscriptError(null);

    try {
      const payload = await updateFieldResponseProcessedTranscriptRow({
        fieldResponseProcessedId: processedId,
        segmentIndex: line.segmentIndex,
        text: nextText,
      });
      const updatedRow = resolveTranscriptRowPayload(payload);

      const nextLines = transcriptLines.map((entry, entryIndex) => {
        if (entry.segmentIndex !== segmentIndex) return entry;
        const normalized =
          toTranscriptLineFromObject(updatedRow, entry.segmentIndex ?? entryIndex) || entry;

        return {
          ...entry,
          ...normalized,
          segmentIndex: entry.segmentIndex ?? normalized.segmentIndex ?? entryIndex,
        };
      });

      setTranscriptHistory((prev) => [...prev, snapshot]);
      setTranscriptLines(nextLines);
      updateSelectedRecordingTranscript(nextLines);
      cancelTranscriptEdit();
    } catch {
      setTranscriptError("Failed to save transcript row. Please try again.");
    } finally {
      setTranscriptSaveRowIndex(null);
    }
  };

  const restoreTranscriptLine = async (segmentIndex) => {
    const line = transcriptLines.find((entry) => entry.segmentIndex === segmentIndex);
    if (!line || line.text === line.originalText) return;

    const processedId =
      selectedRecording?.fieldResponseProcessedId ||
      selectedRecording?.id ||
      selectedRecording?.fieldResponseId;
    if (!processedId) {
      setTranscriptError("Cannot restore transcript row: missing recording id.");
      return;
    }

    const snapshot = cloneTranscriptLines(transcriptLines);
    setTranscriptRestoreRowIndex(segmentIndex);
    setTranscriptError(null);

    try {
      const payload = await restoreFieldResponseProcessedTranscriptRow({
        fieldResponseProcessedId: processedId,
        segmentIndex: line.segmentIndex,
      });
      const restoredRow = resolveTranscriptRowPayload(payload);

      const nextLines = transcriptLines.map((entry, entryIndex) => {
        if (entry.segmentIndex !== segmentIndex) return entry;
        const normalized = toTranscriptLineFromObject(
          restoredRow,
          entry.segmentIndex ?? entryIndex,
        ) || {
          ...entry,
          text: entry.originalText,
          isEdited: false,
        };

        return {
          ...entry,
          ...normalized,
          segmentIndex: entry.segmentIndex ?? normalized.segmentIndex ?? entryIndex,
        };
      });

      setTranscriptHistory((prev) => [...prev, snapshot]);
      setTranscriptLines(nextLines);
      updateSelectedRecordingTranscript(nextLines);
      if (transcriptEditingIndex === segmentIndex) {
        cancelTranscriptEdit();
      }
      setTranscriptToast("Line reverted to original");
    } catch {
      setTranscriptError("Failed to restore transcript row. Please try again.");
    } finally {
      setTranscriptRestoreRowIndex(null);
    }
  };

  const undoTranscriptChanges = () => {
    if (!transcriptHistory.length) return;

    const previousState = transcriptHistory[transcriptHistory.length - 1];
    const restored = cloneTranscriptLines(previousState);

    setTranscriptHistory((prev) => prev.slice(0, -1));
    setTranscriptLines(restored);
    updateSelectedRecordingTranscript(restored);
    cancelTranscriptEdit();
  };

  const toClipHashSeconds = (milliseconds) => {
    const value = toFiniteNumberOrNull(milliseconds);
    if (value == null) return null;
    const seconds = Math.max(0, value / 1000);
    return Number(seconds.toFixed(3)).toString();
  };

  const toClipSeconds = (milliseconds) => {
    const value = toFiniteNumberOrNull(milliseconds);
    if (value == null) return null;
    return Math.max(0, value / 1000);
  };

  const getClipDurationSeconds = (startMs, endMs) => {
    const start = toClipSeconds(startMs);
    const end = toClipSeconds(endMs);
    if (start == null || end == null || end <= start) return null;
    return Number((end - start).toFixed(1));
  };

  const getGroupClipRange = (group) => {
    const start = toClipSeconds(group?.startMs);
    const end = toClipSeconds(group?.endMs);
    const isValid = start != null && end != null && end > start;
    return {
      start,
      end,
      duration: isValid ? end - start : 0,
      isValid,
    };
  };

  const toGroupClipElapsed = (currentTime, range) => {
    if (!range?.isValid) return 0;

    if (currentTime >= range.start - 0.1 && currentTime <= range.end + 0.1) {
      return Math.max(0, Math.min(range.duration, currentTime - range.start));
    }

    return Math.max(0, Math.min(range.duration, currentTime));
  };

  const getGroupClipDisplay = (group) => {
    const range = getGroupClipRange(group);
    if (!range.isValid) {
      return {
        elapsedText: "0:00",
        durationText: "0:00",
        progress: 0,
      };
    }

    const isActive = activeGroupAudioId === group.id;
    const elapsedSeconds = isActive ? toGroupClipElapsed(groupAudioCurrent, range) : 0;
    const safeProgress = range.duration > 0 ? (elapsedSeconds / range.duration) * 100 : 0;

    return {
      elapsedText: formatAudioTime(elapsedSeconds),
      durationText: formatAudioTime(range.duration),
      progress: Math.max(0, Math.min(100, safeProgress)),
    };
  };

  const stopGroupClip = (group) => {
    const player = groupAudioRefs.current[group?.id];
    const range = getGroupClipRange(group);
    if (!player || !range.isValid) return;

    player.pause();
    player.currentTime = range.start;

    if (activeGroupAudioId === group.id) {
      setGroupAudioPlaying(false);
      setGroupAudioCurrent(range.start);
    }
  };

  const seekGroupClip = (event, group) => {
    const player = groupAudioRefs.current[group?.id];
    const range = getGroupClipRange(group);
    if (!player || !range.isValid) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    player.currentTime = range.start + ratio * range.duration;
    setActiveGroupAudioId(group.id);
    setGroupAudioCurrent(player.currentTime);
  };

  const toggleGroupClipPlay = (group) => {
    const player = groupAudioRefs.current[group?.id];
    const range = getGroupClipRange(group);
    if (!player || !range.isValid) return;

    if (activeGroupAudioId && activeGroupAudioId !== group.id) {
      const previousPlayer = groupAudioRefs.current[activeGroupAudioId];
      if (previousPlayer) previousPlayer.pause();
    }

    if (player.currentTime < range.start || player.currentTime >= range.end) {
      player.currentTime = range.start;
    }

    setActiveGroupAudioId(group.id);

    if (!player.paused) {
      player.pause();
      setGroupAudioPlaying(false);
      return;
    }

    player.play();
  };

  const buildClipAudioSource = (baseUrl, startMs, endMs) => {
    if (!baseUrl) return "";

    const start = toClipHashSeconds(startMs);
    const end = toClipHashSeconds(endMs);
    if (!start) return baseUrl;
    if (!end || Number(end) <= Number(start)) return `${baseUrl}#t=${start}`;
    return `${baseUrl}#t=${start},${end}`;
  };

  const surveyQuestionNumberByCampaignQuestionId = useMemo(() => {
    const questionMap = new Map();
    const questions = Array.isArray(campaignQuestionsData?.questions)
      ? campaignQuestionsData.questions
      : [];

    [...questions]
      .sort((left, right) => {
        const leftPosition = Number(left?.position ?? 0);
        const rightPosition = Number(right?.position ?? 0);
        return leftPosition - rightPosition;
      })
      .forEach((question, index) => {
        const campaignQuestionId = toFiniteNumberOrNull(question?.campaignQuestionId);
        if (campaignQuestionId != null) {
          questionMap.set(String(campaignQuestionId), index + 1);
        }
      });

    return questionMap;
  }, [campaignQuestionsData]);

  const transcriptGroupCards = useMemo(() => {
    if (!isTranscriptGrouped || !Array.isArray(transcriptGroups) || !transcriptGroups.length) {
      return [];
    }

    const resolveRowByIndex = (rowIndex, readOnly = false) => {
      if (!Number.isFinite(rowIndex)) return null;
      const normalizedRowIndex = Number(rowIndex);
      const line = transcriptLines[normalizedRowIndex];
      if (!line) return null;
      return {
        line,
        rowIndex: normalizedRowIndex,
        readOnly,
      };
    };

    return transcriptGroups.map((group, groupIndex) => {
      const hasOwnedRange =
        Number.isFinite(group?.firstSegmentIndex) &&
        Number.isFinite(group?.lastSegmentIndex) &&
        group.lastSegmentIndex >= group.firstSegmentIndex;

      const ownedRows = hasOwnedRange
        ? transcriptLines
            .slice(group.firstSegmentIndex, group.lastSegmentIndex + 1)
            .map((line, offset) => ({
              line,
              rowIndex: group.firstSegmentIndex + offset,
              readOnly: false,
            }))
            .filter((entry) => Boolean(entry?.line))
        : [];

      const contextRows =
        ownedRows.length === 0
          ? Array.from(
              new Set(
                (group?.contextSegmentIndexes || [])
                  .map((value) => toFiniteNumberOrNull(value))
                  .filter((value) => value != null),
              ),
            )
              .map((segmentIndex) => resolveRowByIndex(segmentIndex, true))
              .filter((entry) => Boolean(entry?.line))
          : [];

      const rows = ownedRows.length ? ownedRows : contextRows;

      return {
        ...group,
        id: group?.id || `tx-group-${groupIndex}`,
        rows,
        hasOwnedRows: ownedRows.length > 0,
      };
    });
  }, [isTranscriptGrouped, transcriptGroups, transcriptLines]);

  const restoreOriginalTranscript = async () => {
    const processedId =
      selectedRecording?.fieldResponseProcessedId ||
      selectedRecording?.id ||
      selectedRecording?.fieldResponseId;
    if (!processedId) {
      setTranscriptError("Cannot restore original transcript: missing recording id.");
      setShowRestoreTranscriptConfirm(false);
      return;
    }

    const snapshot = cloneTranscriptLines(transcriptLines);
    setTranscriptError(null);
    setIsTranscriptRestoreAllLoading(true);

    try {
      const payload = await restoreFieldResponseProcessedTranscript({
        fieldResponseProcessedId: processedId,
      });
      const grouping = normalizeTranscriptGroups(payload);
      const restored = cloneTranscriptLines(
        normalizeTranscriptLines(payload, selectedRecording?.transcript || ""),
      );

      setTranscriptHistory((prev) => [...prev, snapshot]);
      setTranscriptLines(restored);
      setTranscriptGroups(grouping.groups);
      setIsTranscriptGrouped(grouping.grouped);
      updateSelectedRecordingTranscript(restored);
      cancelTranscriptEdit();
      setShowRestoreTranscriptConfirm(false);
      setTranscriptToast("Original transcript restored");
    } catch {
      setTranscriptError("Failed to restore original transcript. Please try again.");
    } finally {
      setIsTranscriptRestoreAllLoading(false);
    }
  };

  const renderTranscriptLine = ({ line, rowIndex, readOnly = false }) => {
    const isEdited = String(line?.text || "") !== String(line?.originalText || "");
    const segmentIndex = line?.segmentIndex ?? rowIndex;
    const isEditing = !readOnly && transcriptEditingIndex === segmentIndex;

    if (!line) return null;

    return (
      <div
        key={`${segmentIndex}-${readOnly ? "context" : "owned"}`}
        className={`${styles.txLineWrap} ${isEdited ? styles.txLineEdited : ""} ${
          readOnly ? styles.txLineContext : ""
        }`}
        onClick={() => {
          if (readOnly || isEditing) return;
          beginTranscriptEdit(segmentIndex);
        }}
        role={readOnly ? undefined : "button"}
        tabIndex={readOnly ? -1 : 0}
        onKeyDown={(event) => {
          if (readOnly) return;
          if (event.key === "Enter") {
            event.preventDefault();
            beginTranscriptEdit(segmentIndex);
          }
        }}
      >
        {isEditing ? (
          <div className={styles.txEditor} onClick={(event) => event.stopPropagation()}>
            <div className={styles.txSpeaker}>{line.speaker}:</div>
            <textarea
              value={transcriptDraftLine}
              onChange={(event) => setTranscriptDraftLine(event.target.value)}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  cancelTranscriptEdit();
                }

                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  event.stopPropagation();
                  saveTranscriptLine(segmentIndex);
                }
              }}
            />
            <div className={styles.txEditorActions}>
              <div>
                <button
                  type="button"
                  className={styles.txEditorSave}
                  onClick={() => saveTranscriptLine(segmentIndex)}
                  disabled={
                    transcriptSaveRowIndex === segmentIndex ||
                    transcriptRestoreRowIndex === segmentIndex ||
                    isTranscriptRestoreAllLoading
                  }
                >
                  {transcriptSaveRowIndex === segmentIndex ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  className={styles.txEditorCancel}
                  onClick={cancelTranscriptEdit}
                  disabled={transcriptSaveRowIndex === segmentIndex}
                >
                  Cancel
                </button>
              </div>
              <span>Enter to save · Esc to cancel</span>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.txLineContent}>
              <span className={styles.txSpeaker}>{line.speaker}: </span>
              {line.text}
              {isEdited ? <span className={styles.txEditedText}>EDITED</span> : null}
            </div>
            {isEdited && !readOnly ? (
              <button
                type="button"
                className={styles.txRestoreLineBtn}
                onClick={(event) => {
                  event.stopPropagation();
                  restoreTranscriptLine(segmentIndex);
                }}
                aria-label="Restore line"
                disabled={
                  transcriptRestoreRowIndex === segmentIndex ||
                  transcriptSaveRowIndex != null ||
                  isTranscriptRestoreAllLoading
                }
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 101.64-5.2" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 4v5h5" />
                </svg>
              </button>
            ) : null}
          </>
        )}
      </div>
    );
  };

  const saveIdentityField = async ({ field, value }) => {
    if (!selectedRecording || !openedCampaign?.code) return;

    const currentSex = normalizeSexValue(selectedRecording?.sex);
    const currentAgeBand = normalizeAgeBandValue(
      selectedRecording?.ageBand ?? selectedRecording?.ageGroup,
    );

    if (field === "sex" && currentSex === value) return;
    if (field === "ageBand" && currentAgeBand === value) return;

    const processedId =
      selectedRecording?.fieldResponseProcessedId ||
      selectedRecording?.id ||
      selectedRecording?.fieldResponseId;
    if (!processedId) {
      setIdentityError("Cannot update identity: missing recording id.");
      return;
    }

    setIdentitySavingField(field);
    setIdentityError(null);

    try {
      const requestPayload = { fieldResponseProcessedId: processedId };
      requestPayload[field] = value;

      await updateFieldResponseProcessedIdentity(requestPayload);

      const refreshed = await getFieldResponsesProcessed({
        campaignCode: openedCampaign.code,
        page: recordingsPage,
        perPage: RECORDINGS_API_PER_PAGE,
        ...getRecordingListFilterParams(recsFiltersApplied),
        recordingId: recIdSearch,
      });

      setProcessedResponses(refreshed);

      const refreshedRows = Array.isArray(refreshed?.fieldResponseProcessed)
        ? refreshed.fieldResponseProcessed
        : [];
      const matchedItem = refreshedRows.find((row) =>
        doesRecordingMatchApiItem(selectedRecording, row),
      );

      if (matchedItem) {
        const normalizedIdentity = normalizeIdentityFieldsFromApi(matchedItem);
        setSelectedRecording((prev) => (prev ? { ...prev, ...normalizedIdentity } : prev));
      } else {
        setSelectedRecording((prev) => {
          if (!prev) return prev;

          const fallback = { ...prev };
          if (field === "sex") {
            fallback.sex = value;
          } else {
            fallback.ageBand = value;
            fallback.ageGroup = formatAgeBandLabel(value);
          }
          return fallback;
        });
      }
    } catch (error) {
      if (error?.status === 404 || error?.data?.errorCode === "FR1016") {
        setIdentityError("Recording not found. Please refresh and try again.");
      } else if (error?.status === 400 && error?.data?.errorCode === "FR1021") {
        setIdentityError("Pick a different value before saving.");
      } else if (error?.status === 401 || error?.error === "unauthorized") {
        setIdentityError("Unauthorized request. Please sign in again.");
      } else {
        setIdentityError(error?.data?.message || "Failed to update identity. Please try again.");
      }
    } finally {
      setIdentitySavingField(null);
    }
  };

  useEffect(() => {
    if (
      !isCreateModalOpen &&
      !isEndCampaignModalOpen &&
      !isDeleteRecordingConfirmOpen &&
      !isDeleteRecordingSuccessOpen
    ) {
      return undefined;
    }

    const onEscClose = (e) => {
      if (e.key === "Escape") {
        setIsCreateModalOpen(false);
        setIsEndCampaignModalOpen(false);
        setIsDeleteRecordingConfirmOpen(false);
        setIsDeleteRecordingSuccessOpen(false);
      }
    };
    window.addEventListener("keydown", onEscClose);
    return () => window.removeEventListener("keydown", onEscClose);
  }, [
    isCreateModalOpen,
    isEndCampaignModalOpen,
    isDeleteRecordingConfirmOpen,
    isDeleteRecordingSuccessOpen,
  ]);

  const recenterMap = useCallback(() => {
    if (!mapRef.current || !openedCampaign || activeTab !== "map") return;

    // Calculate mapRecordings from processedResponses
    const apiRecords = (processedResponses?.fieldResponseProcessed || []).map((item) => ({
      id: item.id,
      name: item.name || item.respondentName || "Unknown Respondent",
      latitude: item.latitude || item.lat,
      longitude: item.longitude || item.lng,
      sentiment: item.sentiment?.toLowerCase() || "neutral",
      tone: item.tone || item.sentimentTone || "",
      location: item.location || item.address || "Unknown",
      date: item.date || item.dateCreated || "",
      enumerator: item.enumerator || item.collectedBy || "",
      concerns: item.concerns || item.keyConcerns || [],
      summary: item.summary || item.remarks || item.notes || "",
      demographics: {
        ageRange: item.ageRange || item.age || "",
        gender: item.gender || item.sex || "",
        occupation: item.occupation || "",
        yearsInArea: item.yearsInArea || item.yearsInAreaCategory || "",
        socialClass: item.socialClass || item.class || "",
      },
      political: {
        voterType: item.voterType || item.voterStatus || "",
        intent: item.intent || "",
        natGov: item.nationalGovernance || item.natGov || "",
        mayor: item.mayor || item.mayorAssessment || "",
      },
      traits: {
        desirable: Array.isArray(item.desiredTraits)
          ? item.desiredTraits
          : (item.desiredTraits || "").split(/,|;/).filter(Boolean),
        rejected: Array.isArray(item.rejectedTraits)
          ? item.rejectedTraits
          : (item.rejectedTraits || "").split(/,|;/).filter(Boolean),
      },
    }));

    const displayRecords =
      apiRecords.length > 0 ? apiRecords : openedCampaign.details.recordings || [];
    const filteredRecords = displayRecords.filter((rec) =>
      RECORDING_FILTER_DEFS.every((def) => {
        const selected = recsFiltersApplied[def.key] || [];
        if (selected.length === 0) return true;
        const value = def.getValue(rec);
        return value != null && selected.includes(value);
      }),
    );

    const mapRecordings = filteredRecords.filter(
      (rec) => Number.isFinite(rec.latitude) && Number.isFinite(rec.longitude),
    );

    if (!mapRecordings.length) return;

    if (mapRecordings.length === 1) {
      mapRef.current.setCenter({
        lat: mapRecordings[0].latitude,
        lng: mapRecordings[0].longitude,
      });
      mapRef.current.setZoom(14);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    mapRecordings.forEach((rec) => {
      bounds.extend({ lat: rec.latitude, lng: rec.longitude });
    });
    mapRef.current.fitBounds(bounds, 48);
  }, [openedCampaign, processedResponses, activeTab, recsFiltersApplied]);

  useEffect(() => {
    recenterMap();
  }, [recenterMap]);

  const createMapChatThread = (title = "Map scope") => ({
    id: `map-thread-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    title,
    updatedAt: new Date().toISOString(),
  });

  const onCreateMapChatThread = () => {
    const nextThread = createMapChatThread(`Map scope ${mapChatThreads.length + 1}`);
    setMapChatThreads((prev) => [nextThread, ...prev]);
    setMapChatThreadId(nextThread.id);
    setMapChatMessages([
      {
        id: `map-welcome-${nextThread.id}`,
        role: "assistant",
        text: "Map AI is ready. Ask about the respondents currently in view.",
      },
    ]);
    setMapChatInput("");
  };

  const onSendMapChat = () => {
    const prompt = mapChatInput.trim();
    if (!prompt || mapChatTyping) return;

    const threadId = mapChatThreadId || `map-thread-${Date.now()}`;
    const resolvedThread = mapChatThreads.find((entry) => entry.id === threadId) || {
      id: threadId,
      title: prompt.slice(0, 36) || "Map scope",
      updatedAt: new Date().toISOString(),
    };

    if (!mapChatThreads.some((entry) => entry.id === threadId)) {
      setMapChatThreads((prev) => [resolvedThread, ...prev]);
    }

    setMapChatThreadId(threadId);
    setMapChatMessages((prev) => [
      ...prev,
      { id: `map-user-${Date.now()}`, role: "user", text: prompt },
    ]);
    setMapChatInput("");
    setMapChatTyping(true);

    const visibleRespondents = (
      (processedResponses?.fieldResponseProcessed || []).length > 0
        ? processedResponses.fieldResponseProcessed
        : openedCampaign?.details?.recordings || []
    )
      .map((item) => ({
        id: item.id,
        latitude: item.latitude || item.lat,
        longitude: item.longitude || item.lng,
        sentiment: String(item.sentiment || "neutral").toLowerCase(),
        concerns: item.concerns || item.keyConcerns || [],
      }))
      .filter((rec) => Number.isFinite(rec.latitude) && Number.isFinite(rec.longitude))
      .filter((rec) => {
        const bounds = mapRef.current?.getBounds?.();
        if (!bounds) return true;
        return bounds.contains(new window.google.maps.LatLng(rec.latitude, rec.longitude));
      });

    window.setTimeout(() => {
      const answer = getMapScopeAnswer({
        prompt,
        respondents: visibleRespondents,
        campaignCode: openedCampaign?.code || "this campaign",
      });

      setMapChatMessages((prev) => [
        ...prev,
        {
          id: `map-assistant-${Date.now()}`,
          role: "assistant",
          text: answer,
        },
      ]);
      setMapChatTyping(false);
      setMapChatThreads((prev) =>
        prev.map((entry) =>
          entry.id === threadId
            ? {
                ...entry,
                title: entry.title || prompt.slice(0, 36),
                updatedAt: new Date().toISOString(),
              }
            : entry,
        ),
      );
    }, 250);
  };

  const updateMapScopeStats = useCallback(() => {
    if (!mapRef.current || !openedCampaign || activeTab !== "map") {
      setMapScopeStats({ visible: 0, total: 0, zoom: 10 });
      return;
    }

    const apiRecords = (processedResponses?.fieldResponseProcessed || []).map((item) => ({
      id: item.id,
      latitude: item.latitude || item.lat,
      longitude: item.longitude || item.lng,
      sentiment: item.sentiment?.toLowerCase() || "neutral",
      concerns: item.concerns || item.keyConcerns || [],
    }));

    const displayRecords =
      apiRecords.length > 0
        ? apiRecords
        : (openedCampaign.details?.recordings || []).map((item) => ({
            id: item.id,
            latitude: item.latitude || item.lat,
            longitude: item.longitude || item.lng,
            sentiment: item.sentiment?.toLowerCase() || "neutral",
            concerns: item.concerns || item.keyConcerns || [],
          }));

    const visibleRecords = displayRecords.filter((rec) => {
      if (!Number.isFinite(rec.latitude) || !Number.isFinite(rec.longitude)) return false;
      const bounds = mapRef.current?.getBounds?.();
      if (!bounds) return true;
      return bounds.contains(new window.google.maps.LatLng(rec.latitude, rec.longitude));
    });

    setMapScopeStats({
      visible: visibleRecords.length,
      total: displayRecords.length,
      zoom: Number(mapRef.current.getZoom?.() ?? 10),
    });
  }, [openedCampaign, processedResponses, activeTab]);

  useEffect(() => {
    updateMapScopeStats();
  }, [openedCampaign, processedResponses, activeTab, recsFiltersApplied, updateMapScopeStats]);

  useEffect(() => {
    if (!mapChatLogRef.current) return;
    mapChatLogRef.current.scrollTop = mapChatLogRef.current.scrollHeight;
  }, [mapChatMessages, mapChatTyping]);

  const onOpenCreateModal = () => {
    setCreateCampaignError(null);
    setIsCreateModalOpen(true);
  };
  const onCloseCreateModal = () => {
    setCreateCampaignError(null);
    setIsCreateModalOpen(false);
  };
  const selectedProvince = provinceOptions.find(
    (province) => province.code === campaignForm.provinceCode,
  );
  const selectedTarget = municipalityOptions.find(
    (target) => target.code === campaignForm.targetCode,
  );
  const geographyHint =
    campaignForm.provinceCode && campaignForm.targetCode
      ? selectedTarget?.level === "province"
        ? `Province-wide becomes the baseline for ${selectedProvince?.name || "this province"}.`
        : `A city sample refines the campaign within ${selectedProvince?.name || "this province"}.`
      : "";

  useEffect(() => {
    let isMounted = true;

    getGeographyProvinces()
      .then((provinces) => {
        if (!isMounted) return;
        const normalizedProvinces = Array.isArray(provinces)
          ? provinces.map((province) => ({
              code: province?.code || "",
              name: province?.name || "Unknown province",
              regionCode: province?.regionCode || "",
              regionName: province?.regionName || "",
            }))
          : [];

        setProvinceOptions(normalizedProvinces);
      })
      .catch(() => {
        if (!isMounted) return;
        setProvinceOptions([]);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!campaignForm.provinceCode) {
      setMunicipalityOptions([]);
      setCampaignForm((prev) => ({ ...prev, targetCode: "" }));
      return undefined;
    }

    let isMounted = true;

    getGeographyMunicipalities({ provinceCode: campaignForm.provinceCode })
      .then((payload) => {
        if (!isMounted) return;
        const targets = Array.isArray(payload?.targets)
          ? payload.targets.map((target) => ({
              code: target?.code || "",
              name: target?.name || "Unknown municipality",
              level: target?.level || "city_municipality",
            }))
          : [];

        setMunicipalityOptions(targets);
        setCampaignForm((prev) => ({
          ...prev,
          targetCode:
            prev.targetCode && targets.some((target) => target.code === prev.targetCode)
              ? prev.targetCode
              : "",
        }));
      })
      .catch(() => {
        if (!isMounted) return;
        setMunicipalityOptions([]);
        setCampaignForm((prev) => ({ ...prev, targetCode: "" }));
      });

    return () => {
      isMounted = false;
    };
  }, [campaignForm.provinceCode]);

  const onCampaignFormChange = (event) => {
    const { name, value } = event.target;
    setCampaignForm((p) => ({ ...p, [name]: name === "code" ? value.toUpperCase() : value }));
  };
  const onProvinceChange = (event) => {
    const nextProvinceCode = event.target.value;
    setProvinceFieldError("");
    setMunicipalityFieldError("");
    setCampaignForm((prev) => ({
      ...prev,
      provinceCode: nextProvinceCode,
      targetCode: "",
      location: "",
    }));
  };
  const onTargetChange = (event) => {
    const nextTargetCode = event.target.value;
    setMunicipalityFieldError("");
    setCampaignForm((prev) => ({ ...prev, targetCode: nextTargetCode }));
  };
  const onCampaignFileChange = (event) => {
    const file = event.target.files?.[0] ?? null;
    setCampaignForm((prev) => ({ ...prev, surveyDocument: file }));
  };
  const onCreateCampaign = async (e) => {
    e.preventDefault();
    setCreateCampaignError(null);

    const provinceMissing = !campaignForm.provinceCode;
    const municipalityMissing = !campaignForm.provinceCode
      ? "Select a province first."
      : !campaignForm.targetCode
        ? "Please select a municipality / city or Province-Wide."
        : "";

    setProvinceFieldError(provinceMissing ? "Please select a province/state." : "");
    setMunicipalityFieldError(municipalityMissing);

    if (provinceMissing || municipalityMissing) {
      const invalidField = provinceMissing ? provinceFieldRef : municipalityFieldRef;
      invalidField.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const formElement = e.currentTarget;
    if (!formElement.checkValidity()) {
      formElement.reportValidity();
      return;
    }

    setIsCreatingCampaign(true);

    try {
      const campaignDateISO = toCampaignDateISO(campaignForm.date);
      const endDate = new Date(campaignDateISO);
      endDate.setUTCDate(endDate.getUTCDate() + 30);

      const created = await createCampaignRequest({
        description: campaignForm.name,
        campaignCode: campaignForm.code,
        campaignDate: campaignDateISO,
        status: "active",
        endDate: endDate.toISOString(),
        location: campaignForm.location,
        provinceCode: campaignForm.provinceCode,
        targetCode: campaignForm.targetCode,
        surveyType: campaignForm.surveyType,
        campaignType: campaignForm.surveyType === "custom" ? "custom_survey" : "document",
        surveyQuestionDocument: campaignForm.surveyDocument,
      });

      const normalized = normalizeCampaign(created);
      setCampaigns((prev) => {
        const filtered = prev.filter((campaign) => campaign.code !== normalized.code);
        return [normalized, ...filtered];
      });

      setCampaignForm({
        code: "",
        name: "",
        date: "",
        location: "",
        provinceCode: "",
        targetCode: "",
        surveyType: "custom",
        surveyDocument: null,
      });
      setMunicipalityOptions([]);
      setProvinceFieldError("");
      setMunicipalityFieldError("");
      setIsCreateModalOpen(false);

      if (normalized.surveyType === "custom") {
        history.push(Path.getCampaignSurveyBuilder(normalized.code), { campaign: normalized });
      }
    } catch {
      setCreateCampaignError("Failed to create campaign.");
    } finally {
      setIsCreatingCampaign(false);
    }
  };
  const onOpenEndCampaignModal = (c) => {
    setEndCampaignError(null);
    setCampaignToEnd(c);
    setIsEndCampaignModalOpen(true);
  };
  const onCloseEndCampaignModal = () => {
    setEndCampaignError(null);
    setIsEndCampaignModalOpen(false);
    setCampaignToEnd(null);
  };
  const onConfirmEndCampaign = async () => {
    if (!campaignToEnd?.code) return;

    setEndCampaignError(null);
    setIsEndingCampaign(true);

    try {
      const ended = await endCampaignRequest({ campaignCode: campaignToEnd.code });
      const normalized = normalizeCampaign(ended || campaignToEnd);

      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.code === campaignToEnd.code
            ? {
                ...campaign,
                ...normalized,
                status: normalized.status || "ended",
              }
            : campaign,
        ),
      );

      if (openedCampaign?.code === campaignToEnd.code) {
        setOpenedCampaign((prev) =>
          prev
            ? {
                ...prev,
                status: normalized.status || "ended",
              }
            : prev,
        );
      }

      setIsEndCampaignModalOpen(false);
      setCampaignToEnd(null);
    } catch {
      setEndCampaignError("Failed to end campaign.");
    } finally {
      setIsEndingCampaign(false);
    }
  };
  const getPercent = (count, total) => (!total ? 0 : Math.round((count / total) * 100));

  const openRecsFilterPanel = () => {
    setRecsFiltersDraft(cloneRecordingFilterState(recsFiltersApplied));
    setIsRecsFilterOpen(true);
  };

  const closeRecsFilterPanel = () => setIsRecsFilterOpen(false);

  const toggleRecsDraftFilter = (key, value) => {
    setRecsFiltersDraft((prev) => {
      const next = cloneRecordingFilterState(prev);

      if (value === "__all__") {
        next[key] = [];
        return next;
      }

      const selected = new Set(next[key]);
      if (selected.has(value)) {
        selected.delete(value);
      } else {
        selected.add(value);
      }
      next[key] = Array.from(selected);
      return next;
    });
  };

  const resetRecsDraftFilters = () => {
    setRecsFiltersDraft(createRecordingFilterState());
  };

  const updateRecIdSearch = (value) => {
    setRecIdSearch(value);
    setRecordingsPage(1);
  };

  const applyRecsFilters = () => {
    setRecsFiltersApplied(cloneRecordingFilterState(recsFiltersDraft));
    setRecordingsPage(1);
    setIsRecsFilterOpen(false);
  };

  const clearAllRecsFilters = () => {
    const empty = createRecordingFilterState();
    setRecsFiltersApplied(empty);
    setRecsFiltersDraft(empty);
    setRecordingsPage(1);
  };

  const onOpenDeleteRecordingModal = (recording) => {
    setRecordingToDelete(recording || null);
    setDeleteRecordingError(null);
    setIsDeleteRecordingConfirmOpen(true);
  };

  const onCloseDeleteRecordingModal = () => {
    if (isDeletingRecording) return;
    setIsDeleteRecordingConfirmOpen(false);
    setRecordingToDelete(null);
    setDeleteRecordingError(null);
  };

  const onCloseDeleteRecordingSuccessModal = () => {
    setIsDeleteRecordingSuccessOpen(false);
    setRecordingToDelete(null);
    setDeleteRecordingError(null);
  };

  const onConfirmDeleteRecording = async () => {
    if (!recordingToDelete || isDeletingRecording) return;

    const resolvedFieldResponseProcessedId = Number(
      recordingToDelete.fieldResponseProcessedId ?? recordingToDelete.id,
    );

    if (!Number.isFinite(resolvedFieldResponseProcessedId)) {
      setDeleteRecordingError("Recording ID is invalid. Please refresh and try again.");
      return;
    }

    setIsDeletingRecording(true);
    setDeleteRecordingError(null);

    try {
      await deleteFieldResponseProcessed({
        fieldResponseProcessedId: resolvedFieldResponseProcessedId,
      });
    } catch (error) {
      if (error?.status === 404 || error?.data?.errorCode === "FR1016") {
        setDeleteRecordingError("Recording not found. It may have already been deleted.");
      } else if (error?.status === 401 || error?.error === "unauthorized") {
        setDeleteRecordingError("Unauthorized request. Please sign in again.");
      } else {
        setDeleteRecordingError(error?.data?.message || "Failed to delete recording.");
      }
      setIsDeletingRecording(false);
      return;
    }

    const deletingId = String(recordingToDelete.id ?? "");
    const deletingFieldResponseId = String(recordingToDelete.fieldResponseId ?? "");

    const shouldKeepItem = (item) => {
      const candidates = [
        item?.fieldResponseProcessedId,
        item?.fieldResponseId,
        item?.field_response_id,
        item?.id,
      ]
        .map((entry) => String(entry ?? ""))
        .filter(Boolean);

      if (deletingId && candidates.includes(deletingId)) return false;
      if (deletingFieldResponseId && candidates.includes(deletingFieldResponseId)) return false;
      return true;
    };

    setProcessedResponses((prev) => {
      if (!prev || !Array.isArray(prev?.fieldResponseProcessed)) return prev;

      const previousRows = prev.fieldResponseProcessed;
      const nextRows = previousRows.filter((item) => shouldKeepItem(item));
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount <= 0) return prev;

      const next = { ...prev, fieldResponseProcessed: nextRows };
      const totalKeys = ["total", "totalCount", "count", "total_count"];
      totalKeys.forEach((key) => {
        if (typeof prev?.[key] === "number") {
          next[key] = Math.max(0, prev[key] - removedCount);
        }
      });

      return next;
    });

    setOpenedCampaign((prev) => {
      if (!prev || !Array.isArray(prev?.details?.recordings)) return prev;

      const previousRows = prev.details.recordings;
      const nextRows = previousRows.filter((item) => shouldKeepItem(item));
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount <= 0) return prev;

      const previousTotal = Number(prev?.details?.totalRecordings ?? 0);

      return {
        ...prev,
        details: {
          ...prev.details,
          recordings: nextRows,
          totalRecordings: Number.isFinite(previousTotal)
            ? Math.max(0, previousTotal - removedCount)
            : prev?.details?.totalRecordings,
        },
      };
    });

    if (
      selectedRecording &&
      (String(selectedRecording.id ?? "") === deletingId ||
        String(selectedRecording.fieldResponseId ?? "") === deletingFieldResponseId)
    ) {
      setSelectedRecording(null);
    }

    setIsDeletingRecording(false);
    setDeleteRecordingError(null);
    setIsDeleteRecordingConfirmOpen(false);
    setIsDeleteRecordingSuccessOpen(true);
  };

  const refreshChatThreadsForCampaign = async ({ campaignCode, preferredThreadId = null }) => {
    if (!campaignCode) return [];

    const threadsResult = await getChatThreads({ campaignCode, page: 1, perPage: 20 });
    const threads = sortThreadsByRecent(threadsResult?.threads || []).map((thread, index) =>
      mapChatThread(thread, index),
    );

    setChatThreads(threads);

    if (
      preferredThreadId &&
      threads.some((thread) => thread?.insightChatThreadId === preferredThreadId)
    ) {
      setChatThreadId(preferredThreadId);
    }

    return threads;
  };

  const initializeChatForCampaign = async (campaignCode) => {
    const sequence = ++chatInitSequenceRef.current;

    setChatInitializing(true);
    setChatTyping(false);
    setChatThreads([]);
    setChatThreadId(null);
    setChatMessages([]);

    try {
      let threads = await refreshChatThreadsForCampaign({ campaignCode });
      if (sequence !== chatInitSequenceRef.current) return;

      const latestThread = resolveLatestThread(threads);
      const createdThread = latestThread ? null : await createChatThread({ campaignCode });
      if (sequence !== chatInitSequenceRef.current) return;

      if (createdThread?.insightChatThreadId) {
        threads = await refreshChatThreadsForCampaign({
          campaignCode,
          preferredThreadId: createdThread.insightChatThreadId,
        });
        if (sequence !== chatInitSequenceRef.current) return;
      }

      const resolvedThread =
        (createdThread?.insightChatThreadId
          ? threads.find(
              (thread) => thread?.insightChatThreadId === createdThread.insightChatThreadId,
            )
          : null) || resolveLatestThread(threads);
      const resolvedThreadId = resolvedThread?.insightChatThreadId;

      if (!resolvedThreadId) {
        setChatMessages([getWelcomeChatMessage(campaignCode)]);
        return;
      }

      setChatThreadId(resolvedThreadId);

      const messagesResult = await getChatThreadMessages({
        insightChatThreadId: resolvedThreadId,
        page: 1,
        perPage: 100,
      });
      if (sequence !== chatInitSequenceRef.current) return;

      const mappedMessages = Array.isArray(messagesResult?.messages)
        ? messagesResult.messages.map(mapApiChatMessage)
        : [];

      setChatMessages(
        mappedMessages.length > 0 ? mappedMessages : [getWelcomeChatMessage(campaignCode)],
      );
    } catch {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatThreads([]);
      setChatThreadId(null);
      setChatMessages([getWelcomeChatMessage(campaignCode)]);
    } finally {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatInitializing(false);
    }
  };

  const loadChatThreadMessagesForCampaign = async ({
    campaignCode,
    insightChatThreadId,
    keepInput = false,
  }) => {
    if (!campaignCode || !insightChatThreadId) return;

    const sequence = ++chatInitSequenceRef.current;

    setChatInitializing(true);
    setChatTyping(false);
    if (!keepInput) {
      setChatInput("");
    }
    setChatThreadId(insightChatThreadId);

    try {
      const messagesResult = await getChatThreadMessages({
        insightChatThreadId,
        page: 1,
        perPage: 100,
      });
      if (sequence !== chatInitSequenceRef.current) return;

      const mappedMessages = Array.isArray(messagesResult?.messages)
        ? messagesResult.messages.map(mapApiChatMessage)
        : [];

      setChatMessages(
        mappedMessages.length > 0 ? mappedMessages : [getWelcomeChatMessage(campaignCode)],
      );
    } catch {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatMessages([getWelcomeChatMessage(campaignCode)]);
    } finally {
      if (sequence !== chatInitSequenceRef.current) return;
      setChatInitializing(false);
    }
  };

  const onSelectChatThread = async (insightChatThreadId) => {
    if (!openedCampaign || !insightChatThreadId || chatInitializing || chatTyping) return;
    if (insightChatThreadId === chatThreadId) return;

    await loadChatThreadMessagesForCampaign({
      campaignCode: openedCampaign.code,
      insightChatThreadId,
    });
  };

  const onCreateNewChatThread = async () => {
    if (!openedCampaign || chatInitializing || chatTyping) return;

    const campaignCode = openedCampaign.code;

    setChatInitializing(true);
    setChatTyping(false);
    setChatInput("");

    try {
      const createdThread = await createChatThread({ campaignCode });
      const createdThreadId = createdThread?.insightChatThreadId;

      if (!createdThreadId) {
        setChatMessages([getWelcomeChatMessage(campaignCode)]);
        return;
      }

      await refreshChatThreadsForCampaign({
        campaignCode,
        preferredThreadId: createdThreadId,
      });

      await loadChatThreadMessagesForCampaign({
        campaignCode,
        insightChatThreadId: createdThreadId,
        keepInput: true,
      });
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-error-${Date.now()}`,
          role: "assistant",
          text: "I couldn't start a new thread right now. Please try again.",
        },
      ]);
      setChatInitializing(false);
    }
  };

  const openCampaign = (campaign, options = {}) => {
    const { skipNavigation = false } = options;
    const code = String(campaign?.code || "").trim();

    if (!skipNavigation && code) {
      const targetPath = `/${encodeURIComponent(code)}`;
      if (location.pathname !== targetPath) {
        history.push(targetPath);
      }
    }

    setOpenedCampaign(campaign);
    setActiveTab("info");
    setRecordingsPage(1);
    setRecIdSearch("");
    setChatInput("");
    setChatTyping(false);
    setChatThreads([]);
    setChatThreadId(null);
    setChatInitializing(false);
    initializeChatForCampaign(campaign.code);
    setAnalytics(null);
    setProcessedResponses(null);
    setRecordingsFilterOptions(null);
    setAnalyticsError(null);
    setIsRecsFilterOpen(false);
    setRecsFiltersApplied(createRecordingFilterState());
    setRecsFiltersDraft(createRecordingFilterState());
    if (listAudioRef.current) {
      listAudioRef.current.pause();
      listAudioRef.current.currentTime = 0;
    }
    setInlineAudioRecordId(null);
    setInlineAudioPlaying(false);
    setInlineAudioCurrent(0);
    setInlineAudioDuration(0);
    setCampaignQuestionsData(null);
    setCampaignQuestionsLoading(false);
    setCampaignType(null);
    setCampaignTypeLoading(false);
    setAnalyticsLoading(true);

    if (campaign.code) {
      setCampaignTypeLoading(true);
      getCampaignByCodeRequest({ campaignCode: campaign.code })
        .then((data) => {
          const resolvedCampaignType = resolveCampaignType(data);
          setCampaignType(resolvedCampaignType);

          if (resolvedCampaignType === "custom_survey" && campaign.campaignId) {
            setCampaignQuestionsLoading(true);
            getCampaignQuestionsRequest({ campaignId: campaign.campaignId })
              .then((questionsData) => {
                setCampaignQuestionsData(questionsData || null);
              })
              .catch(() => {
                setCampaignQuestionsData(null);
              })
              .finally(() => {
                setCampaignQuestionsLoading(false);
              });
          }
        })
        .catch(() => {
          setCampaignType(null);
        })
        .finally(() => {
          setCampaignTypeLoading(false);
        });
    }

    getTotalProcessedResponses({ campaignCode: campaign.code })
      .then((total) =>
        Promise.all([
          Promise.resolve(total),
          getCategoryDistribution({ campaignCode: campaign.code }),
          getSentimentDistribution({ campaignCode: campaign.code }),
          getEmploymentSectorDistribution({ campaignCode: campaign.code }),
          getEnumeratorPerformance({ campaignCode: campaign.code }),
          getKeyConcerns({ campaignCode: campaign.code }),
          getAgeDistribution({ campaignCode: campaign.code }),
          getSexDistribution({ campaignCode: campaign.code }),
        ]),
      )
      .then(
        ([total, category, sentiment, employment, enumeratorPerformance, concerns, age, sex]) => {
          setAnalytics({
            total,
            category,
            sentiment,
            employment,
            enumeratorPerformance,
            concerns,
            age,
            sex,
          });
        },
      )
      .catch(() => {
        setAnalyticsError("Failed to load analytics data.");
      })
      .finally(() => {
        setAnalyticsLoading(false);
      });
  };

  const clearOpenedCampaignState = () => {
    setOpenedCampaign(null);
    setRecordingsPage(1);
    setRecIdSearch("");
    setAnalytics(null);
    setProcessedResponses(null);
    setRecordingsFilterOptions(null);
    setAnalyticsError(null);
    setChatInput("");
    setChatMessages([]);
    setChatTyping(false);
    setChatThreads([]);
    setChatThreadId(null);
    setChatInitializing(false);
    chatInitSequenceRef.current += 1;
    setCampaignQuestionsData(null);
    setCampaignQuestionsLoading(false);
    setCampaignType(null);
    setCampaignTypeLoading(false);
    setIsRecsFilterOpen(false);
    setRecsFiltersApplied(createRecordingFilterState());
    setRecsFiltersDraft(createRecordingFilterState());
    if (listAudioRef.current) {
      listAudioRef.current.pause();
      listAudioRef.current.currentTime = 0;
    }
    setInlineAudioRecordId(null);
    setInlineAudioPlaying(false);
    setInlineAudioCurrent(0);
    setInlineAudioDuration(0);
  };

  const goBack = () => {
    if (location.pathname !== Path.Campaigns) {
      history.push(Path.Campaigns);
    }

    clearOpenedCampaignState();
  };

  const onOpenSurveyBuilder = (campaign) => {
    const code = String(campaign?.code || "").trim();
    if (!code) return;

    const isCustomSurvey = campaignType === "custom_survey";

    history.push(Path.getCampaignSurveyBuilder(code), {
      campaign: {
        ...campaign,
        campaignType: isCustomSurvey ? "custom_survey" : campaign?.campaignType,
        surveyType: isCustomSurvey ? "custom" : campaign?.surveyType,
      },
      questions: campaignQuestionsData?.questions || null,
    });
  };

  useEffect(() => {
    if (campaignCodeParam) return;
    if (!openedCampaign) return;

    // Browser back from /{campaignCode} -> /campaigns should return to list state.
    clearOpenedCampaignState();
  }, [campaignCodeParam, openedCampaign]);

  useEffect(() => {
    if (!campaignCodeParam || !campaigns.length) return;

    const decodedCode = decodeURIComponent(campaignCodeParam);
    if (openedCampaign?.code === decodedCode) return;

    const matchingCampaign = campaigns.find(
      (campaign) => String(campaign.code || "").toLowerCase() === decodedCode.toLowerCase(),
    );

    if (matchingCampaign) {
      openCampaign(matchingCampaign, { skipNavigation: true });
    }
    // openCampaign is intentionally excluded to avoid re-running campaign bootstrap repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignCodeParam, campaigns, openedCampaign]);

  const totalRecordingsFromApi = campaigns.reduce(
    (sum, campaign) => sum + (campaignRecordingTotals[campaign.code] ?? 0),
    0,
  );

  const totalEnumeratorsFromList = campaigns.reduce((sum, campaign) => {
    const count =
      campaignListEnumeratorCounts[campaign.code] ?? parseEnumeratorCount(campaign.enumerators);
    return sum + count;
  }, 0);

  const onSendChat = async () => {
    const prompt = chatInput.trim();
    if (!prompt || !openedCampaign || chatTyping || chatInitializing) return;

    const campaign = openedCampaign;

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: prompt,
    };

    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    setChatTyping(true);

    try {
      let resolvedThreadId = chatThreadId;

      if (!resolvedThreadId) {
        const thread = await createChatThread({ campaignCode: campaign.code });
        resolvedThreadId = thread?.insightChatThreadId;
        setChatThreadId(resolvedThreadId || null);

        if (resolvedThreadId) {
          await refreshChatThreadsForCampaign({
            campaignCode: campaign.code,
            preferredThreadId: resolvedThreadId,
          });
        }
      }

      if (!resolvedThreadId) {
        throw new Error("Missing thread id");
      }

      const response = await createChatThreadMessage({
        insightChatThreadId: resolvedThreadId,
        prompt,
      });

      const assistantMessage = response?.assistantMessage;
      const assistantText = assistantMessage?.content || "I could not generate a response.";

      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-${assistantMessage?.insightChatMessageId ?? Date.now()}`,
          role: "assistant",
          text: assistantText,
        },
      ]);

      await refreshChatThreadsForCampaign({
        campaignCode: campaign.code,
        preferredThreadId: resolvedThreadId,
      });

      const combinedInsightText = `${prompt} ${assistantText}`;
      const mapMentioned = hasMapMention(combinedInsightText);

      if (mapMentioned) {
        const insightRecords = (processedResponses?.fieldResponseProcessed || []).map((item) => ({
          sentiment: String(item?.sentiment || item?.tone || "neutral").toLowerCase(),
          sex: item?.sex || item?.gender || "",
          ageGroup:
            item?.ageGroup ||
            item?.age_group ||
            toAgeGroupFromAge(item?.age || item?.respondentAge || item?.demographics?.age),
          socialClass: item?.socialClass || item?.social_class || "",
          areaType: item?.areaType || item?.area_type || "",
          civilStatus: item?.civilStatus || item?.civil_status || "",
          occupation: item?.occupation || "",
          primaryMedia:
            item?.primaryMedia || item?.mediaSource || item?.media || item?.sourceOfInfo || "",
          voterStatus: item?.voterStatus || item?.voter_status || "",
          voteIntent: item?.voteIntent || item?.vote_intent || "",
        }));

        const insightFilters = extractInsightFiltersFromText(assistantText, insightRecords);
        const hasAnyFilter = Object.values(insightFilters).some(
          (values) => Array.isArray(values) && values.length > 0,
        );

        if (hasAnyFilter) {
          setRecsFiltersApplied(insightFilters);
          setRecsFiltersDraft(cloneRecordingFilterState(insightFilters));
          setRecordingsPage(1);
        }

        setActiveTab("map");
      }
    } catch {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `ai-error-${Date.now()}`,
          role: "assistant",
          text: "I couldn't process that request right now. Please try again.",
        },
      ]);
    } finally {
      setChatTyping(false);
    }
  };

  const endCampaignModal = isEndCampaignModalOpen && campaignToEnd && (
    <div className={styles.modalRoot} role="presentation" onMouseDown={onCloseEndCampaignModal}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="end-campaign-title"
        className={styles.modalContent}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <h2 id="end-campaign-title">End Campaign</h2>
        </div>
        <div className={styles.endCampaignBody}>
          <p className={styles.endCampaignMessage}>
            Are you sure you want to end{" "}
            <span className={styles.campaignNameHighlight}>{`"${campaignToEnd.title}"`}</span>?
          </p>
          <div className={styles.warningBox}>
            <p className={styles.warningText}>
              This will mark the campaign as ended. No new recordings can be submitted under this
              campaign code.
            </p>
          </div>
          {endCampaignError && (
            <div className={styles.analyticsError} style={{ marginBottom: 10 }}>
              {endCampaignError}
            </div>
          )}
          <div className={styles.modalFooter}>
            <button type="button" className={styles.cancelButton} onClick={onCloseEndCampaignModal}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.endConfirmButton}
              onClick={onConfirmEndCampaign}
              disabled={isEndingCampaign}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className={styles.endIconSmall}
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10" />
                <rect x="9" y="9" width="6" height="6" rx="1" />
              </svg>
              {isEndingCampaign ? "Ending..." : "End Campaign"}
            </button>
          </div>
        </div>
        <button type="button" className={styles.closeButton} onClick={onCloseEndCampaignModal}>
          <span aria-hidden="true">x</span>
          <span className={styles.srOnly}>Close</span>
        </button>
      </div>
    </div>
  );

  const createModal = isCreateModalOpen && (
    <div className={styles.modalRoot} role="presentation" onMouseDown={onCloseCreateModal}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-campaign-title"
        className={`${styles.modalContent} ${styles.createBox}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div id="create-campaign-title" className={styles.createTitle}>
          Create New Campaign
        </div>
        <form className={styles.createBody} onSubmit={onCreateCampaign}>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-code">
              Campaign Code (max 6 chars)
            </label>
            <input
              className={styles.fieldInput}
              id="nc-code"
              name="code"
              maxLength={6}
              placeholder="e.g. LAG004"
              value={campaignForm.code}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-name">
              Campaign Name
            </label>
            <input
              className={styles.fieldInput}
              id="nc-name"
              name="name"
              placeholder="e.g. North Laguna Pulse Check"
              value={campaignForm.name}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-date">
              Campaign Start Date
            </label>
            <input
              className={styles.fieldInput}
              type="date"
              id="nc-date"
              name="date"
              value={campaignForm.date}
              onChange={onCampaignFormChange}
              required
            />
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-province">
              Province / State
            </label>
            <select
              className={styles.fieldInput}
              id="nc-province"
              name="provinceCode"
              value={campaignForm.provinceCode}
              onChange={onProvinceChange}
              ref={provinceFieldRef}
              style={provinceFieldError ? { borderColor: "#ef4444" } : undefined}
              required
            >
              <option value="">Select a province / state...</option>
              {provinceOptions.map((province) => (
                <option key={province.code} value={province.code}>
                  {province.name}
                </option>
              ))}
            </select>
            {provinceFieldError && (
              <div className={styles.analyticsError}>{provinceFieldError}</div>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-target">
              Municipality / City
            </label>
            <select
              className={styles.fieldInput}
              id="nc-target"
              name="targetCode"
              value={campaignForm.targetCode}
              onChange={onTargetChange}
              ref={municipalityFieldRef}
              disabled={!campaignForm.provinceCode}
              style={municipalityFieldError ? { borderColor: "#ef4444" } : undefined}
              required={Boolean(campaignForm.provinceCode)}
            >
              <option value="">
                {campaignForm.provinceCode
                  ? "Select a municipality / city..."
                  : "Select a province first"}
              </option>
              {municipalityOptions.map((target) => (
                <option key={target.code} value={target.code}>
                  {target.name}
                </option>
              ))}
            </select>
            {municipalityFieldError ? (
              <div className={styles.analyticsError}>{municipalityFieldError}</div>
            ) : geographyHint ? (
              <div className={styles.fieldHint}>{geographyHint}</div>
            ) : null}
          </div>
          <div className={styles.fieldGroup}>
            <div className={styles.fieldLabel}>Survey Type</div>
            <div className={styles.surveyTypeGrid} role="radiogroup" aria-label="Survey Type">
              {SURVEY_TYPE_OPTIONS.map((option) => {
                const checked = campaignForm.surveyType === option.value;

                return (
                  <label
                    key={option.value}
                    className={`${styles.surveyTypeCard} ${checked ? styles.surveyTypeCardActive : ""}`}
                  >
                    <input
                      className={styles.surveyTypeInput}
                      type="radio"
                      name="surveyType"
                      value={option.value}
                      checked={checked}
                      onChange={onCampaignFormChange}
                    />
                    <span className={styles.surveyTypeCardTop}>
                      <span className={styles.surveyTypeIcon} aria-hidden="true">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.9"
                        >
                          <rect x="4" y="5" width="16" height="14" rx="3" />
                          <path d="M8 9h8M8 12h6M8 15h4" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className={styles.surveyTypeCheck} aria-hidden="true" />
                    </span>
                    <span className={styles.surveyTypeTitle}>{option.label}</span>
                    <span className={styles.surveyTypeDescription}>{option.description}</span>
                  </label>
                );
              })}
            </div>
            <div className={styles.surveyTypeHint}>
              {campaignForm.surveyType === "custom"
                ? "After creating, you can continue into the Survey Builder to compose questions, answer options, and branching flow for enumerators."
                : "Use Simple Survey when enumerators only need free-form recordings, with an optional survey guide document attached below."}
            </div>
          </div>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor="nc-doc">
              Survey Questions Document (Optional)
            </label>
            <input
              className={`${styles.fieldInput} ${styles.fileInput}`}
              id="nc-doc"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={onCampaignFileChange}
            />
          </div>
          <div className={styles.createActions}>
            <button
              type="button"
              className={styles.createCancelButton}
              onClick={onCloseCreateModal}
              disabled={isCreatingCampaign}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.createSubmitButton}
              disabled={isCreatingCampaign}
            >
              {isCreatingCampaign
                ? "Creating..."
                : campaignForm.surveyType === "custom"
                  ? "Create & Build Survey"
                  : "Create Campaign"}
            </button>
          </div>
          {createCampaignError && (
            <div className={styles.analyticsError}>{createCampaignError}</div>
          )}
        </form>
      </div>
    </div>
  );

  /* ── CAMPAIGN DETAIL VIEW ── */
  if (openedCampaign) {
    const c = openedCampaign;
    const d = c.details;
    const statusColor = c.status === "active" ? "#10b981" : "#6b7280";
    const totalProcessed = resolveCount(analytics?.total, ["totalProcessedResponses"]);
    const resolvedSentiment = resolveSentimentBreakdown(analytics?.sentiment);
    const apiRecordings = (processedResponses?.fieldResponseProcessed || []).map((item, index) => {
      const pickField = (...values) => {
        for (const value of values) {
          if (value == null) continue;
          if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed) return trimmed;
            continue;
          }
          if (Array.isArray(value)) {
            if (value.length > 0) return value;
            continue;
          }
          return value;
        }
        return "";
      };

      const parseList = (value) => {
        if (Array.isArray(value)) {
          return value.map((entry) => String(entry || "").trim()).filter(Boolean);
        }
        if (!value) return [];
        return String(value)
          .split(/,|;/)
          .map((entry) => entry.trim())
          .filter(Boolean);
      };

      const sentiment = String(item?.sentiment || item?.tone || "neutral").toLowerCase();
      const safeSentiment = ["positive", "negative", "neutral"].includes(sentiment)
        ? sentiment
        : "neutral";

      const timestampCandidate = pickField(
        item?.timestamp,
        item?.processedAt,
        item?.createdAt,
        item?.recordedAt,
        item?.submittedAt,
        item?.surveyDateTime,
        item?.responseDateTime,
        item?.dateTime,
      );
      const parsedTimestamp = timestampCandidate ? new Date(timestampCandidate) : null;
      const hasValidTimestamp =
        parsedTimestamp instanceof Date && !Number.isNaN(parsedTimestamp.getTime());

      const dateStr = hasValidTimestamp
        ? parsedTimestamp.toISOString().split("T")[0]
        : pickField(item?.date, item?.recordingDate, item?.surveyDate, item?.dateCreated) || "-";
      const timeStr = hasValidTimestamp
        ? parsedTimestamp.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
        : pickField(item?.time, item?.recordingTime, item?.surveyTime, item?.timeCreated, "");

      const enumerator = item?.name || item?.userId || "Unknown Enumerator";
      const initials =
        enumerator
          .split(/[\s_-]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join("") || "UE";
      const lat = item?.latitude ?? item?.lat ?? null;
      const lng = item?.longitude ?? item?.lng ?? null;
      const coordsStr =
        lat != null && lng != null ? `${Number(lat).toFixed(4)}, ${Number(lng).toFixed(4)}` : null;
      const concernChips = parseList(
        pickField(item?.keyConcerns, item?.key_concerns, item?.concerns, item?.issues),
      );

      const desiredTraits = parseList(
        pickField(
          item?.desiredTraits,
          item?.desired_traits,
          item?.leaderPreferences?.desiredTraits,
          item?.leaderPreferences?.desired,
        ),
      );
      const rejectedTraits = parseList(
        pickField(
          item?.rejectedTraits,
          item?.rejected_traits,
          item?.leaderPreferences?.rejectedTraits,
          item?.leaderPreferences?.rejected,
        ),
      );

      const derivedAgeGroup = toAgeGroupFromAge(
        pickField(item?.age, item?.respondentAge, item?.demographics?.age, item?.ageYears),
      );
      const normalizedIdentity = normalizeIdentityFieldsFromApi(item);

      return {
        id: item?.fieldResponseProcessedId || item?.fieldResponseId || `rec-${index}`,
        fieldResponseProcessedId: pickField(
          item?.fieldResponseProcessedId,
          item?.field_response_processed_id,
          item?.fieldResponseId,
          item?.field_response_id,
        ),
        fieldResponseId: pickField(item?.fieldResponseId, item?.field_response_id),
        enumerator,
        initials,
        location: pickField(item?.address, item?.location, item?.place) || "Unknown Location",
        barangay: pickField(item?.barangay, item?.village, item?.brgy),
        identifiedBarangay: pickField(item?.identifiedBarangay, item?.identified_barangay),
        latitude: lat != null ? Number(lat) : null,
        longitude: lng != null ? Number(lng) : null,
        coordsStr,
        sentiment: safeSentiment,
        category: pickField(item?.category, item?.classification),
        tone: pickField(item?.tone, item?.sentimentTone),
        score: Number(item?.sentimentScore ?? item?.score ?? 0),
        concern: concernChips.length > 0 ? concernChips.join(", ") : "-",
        concernChips,
        date: dateStr,
        time: timeStr,
        dateRaw: hasValidTimestamp ? parsedTimestamp.toISOString() : null,
        timestampRaw: pickField(
          item?.timestamp,
          item?.processedAt,
          item?.createdAt,
          item?.recordedAt,
          item?.submittedAt,
          item?.surveyDateTime,
          item?.responseDateTime,
          item?.dateTime,
        ),
        processedAtRaw: pickField(item?.processedAt, item?.processed_at),
        createdAtRaw: pickField(item?.createdAt, item?.created_at),
        updatedAtRaw: pickField(item?.updatedAt, item?.updated_at),
        deletedAtRaw: pickField(item?.deletedAt, item?.deleted_at),
        ageGroup:
          normalizedIdentity.ageGroup ||
          pickField(
            item?.ageGroup,
            item?.age_group,
            item?.ageBracket,
            item?.age_bracket,
            item?.demographics?.ageGroup,
            item?.demographics?.ageBracket,
            derivedAgeGroup,
          ),
        ageBand: normalizedIdentity.ageBand,
        age: pickField(item?.age, item?.respondentAge, item?.demographics?.age, item?.ageYears),
        sex:
          normalizedIdentity.sex === undefined
            ? pickField(item?.sex, item?.gender, item?.demographics?.gender, item?.respondentSex)
            : normalizedIdentity.sex,
        originalSex: normalizedIdentity.originalSex,
        sexEdited: normalizedIdentity.sexEdited,
        sexEditedAt: normalizedIdentity.sexEditedAt,
        sexEditedBy: normalizedIdentity.sexEditedBy,
        originalAgeBand: normalizedIdentity.originalAgeBand,
        ageBandEdited: normalizedIdentity.ageBandEdited,
        ageBandEditedAt: normalizedIdentity.ageBandEditedAt,
        ageBandEditedBy: normalizedIdentity.ageBandEditedBy,
        occupation: pickField(item?.occupation, item?.occupationType, item?.jobTitle),
        employmentSector: pickField(
          item?.employmentSector,
          item?.employment_sector,
          item?.employment,
          item?.occupationSector,
        ),
        residency: pickField(
          item?.residency,
          item?.yearsInArea,
          item?.years_in_area,
          item?.yearsOfResidency,
          item?.residencyYears,
        ),
        summary: pickField(item?.summary, item?.briefSummary),
        transcript: pickField(item?.transcript, item?.transcription),
        audioUrl: pickField(item?.audioUrl, item?.audio_url, item?.recordingUrl, item?.audio),
        respondentAudioUrl: pickField(item?.respondentAudioUrl, item?.respondent_audio_url),
        respondentName: pickField(item?.respondentName, item?.respondent, item?.name),
        userId: pickField(item?.userId, item?.user_id),
        speakerRoleMap: pickField(item?.speakerRoleMap, item?.speaker_role_map),
        campaignCode: pickField(item?.campaignCode, item?.campaign_code) || c.code,
        civilStatus: pickField(
          item?.civilStatus,
          item?.civil_status,
          item?.maritalStatus,
          item?.marital_status,
        ),
        dependents: pickField(
          item?.dependents,
          item?.numberOfDependents,
          item?.noOfDependents,
          item?.householdDependents,
        ),
        yearsInArea: pickField(
          item?.yearsInArea,
          item?.years_in_area,
          item?.yearsOfResidency,
          item?.residencyYears,
          item?.residency,
        ),
        areaType: pickField(
          item?.areaType,
          item?.area_type,
          item?.areaClassification,
          item?.urbanRural,
        ),
        socialClass: pickField(
          item?.socialClass,
          item?.social_class,
          item?.socioEconomicClass,
          item?.socio_economic_class,
          item?.secClass,
        ),
        monthlyIncome: pickField(
          item?.monthlyIncome,
          item?.monthly_income,
          item?.incomeBracket,
          item?.monthly_income_bracket,
        ),
        housing: pickField(
          item?.housing,
          item?.housingType,
          item?.housing_type,
          item?.residenceType,
        ),
        educationAttainment: pickField(
          item?.educationAttainment,
          item?.educationalAttainment,
          item?.education,
          item?.highestEducation,
        ),
        fieldOfStudy: pickField(item?.fieldOfStudy, item?.field_of_study, item?.course),
        primaryPlatform: pickField(
          item?.primaryPlatform,
          item?.primaryMedia,
          item?.mediaSource,
          item?.media,
          item?.sourceOfInfo,
        ),
        religion: pickField(item?.religion, item?.religiousAffiliation),
        ethnicityLanguage: pickField(
          item?.ethnicityLanguage,
          item?.ethnicity,
          item?.language,
          item?.ethnicity_or_language,
        ),
        voterStatus: pickField(item?.voterStatus, item?.voter_status),
        voteIntent: pickField(item?.voteIntent, item?.vote_intent),
        voteIntentStrength: pickField(
          item?.voteIntentStrength,
          item?.vote_intent_strength,
          item?.intentStrength,
        ),
        nationalGov: pickField(item?.nationalGov, item?.national_gov, item?.nationalGovRating),
        governor: pickField(item?.governor, item?.localGovGovernor, item?.governorRating),
        mayor: pickField(item?.mayor, item?.localGovMayor, item?.mayorRating),
        coreValues: pickField(item?.coreValues, item?.core_values, item?.leaderCoreValues),
        leaderArchetype: pickField(
          item?.leaderArchetype,
          item?.leader_archetype,
          item?.archetypePreference,
        ),
        desiredTraits,
        rejectedTraits,
      };
    });
    const uiRecordings = apiRecordings.length > 0 ? apiRecordings : d.recordings;
    const recsFilterGroups = RECORDING_FILTER_DEFS.map((def) => {
      const options = [...(recordingsFilterOptions?.[def.key] || [])].sort((a, b) => {
        if (Array.isArray(def.sortOrder)) {
          const aIdx = def.sortOrder.indexOf(a.value);
          const bIdx = def.sortOrder.indexOf(b.value);
          const safeA = aIdx < 0 ? Number.MAX_SAFE_INTEGER : aIdx;
          const safeB = bIdx < 0 ? Number.MAX_SAFE_INTEGER : bIdx;
          if (safeA !== safeB) return safeA - safeB;
        }
        if (b.count !== a.count) return b.count - a.count;
        return String(a.label).localeCompare(String(b.label));
      });

      return { ...def, options };
    });

    const filteredRecordings = uiRecordings;

    const exportRecordingsAsJson = () => {
      const payload = filteredRecordings.map((rec) => {
        const exportedTimestamp =
          rec.timestampRaw || formatExportDateTime(rec.dateRaw) || formatExportDateTime(rec.date);
        const parsedProcessedId = Number(rec.fieldResponseProcessedId ?? rec.id);
        const parsedFieldResponseId = Number(
          rec.fieldResponseProcessedId ?? rec.fieldResponseId ?? rec.id,
        );
        const parsedAge = Number(rec.age);
        const normalizedSpeakerRoleMap =
          rec.speakerRoleMap == null
            ? "{}"
            : typeof rec.speakerRoleMap === "string"
              ? rec.speakerRoleMap
              : JSON.stringify(rec.speakerRoleMap);

        return {
          field_response_processed_id: Number.isFinite(parsedProcessedId)
            ? parsedProcessedId
            : null,
          field_response_id: Number.isFinite(parsedFieldResponseId) ? parsedFieldResponseId : null,
          transcript: rec.transcript || "",
          sentiment: rec.sentiment || "neutral",
          category: rec.category || "other",
          employment_sector: rec.employmentSector || "unknown",
          tone: rec.tone || "neutral",
          summary: rec.summary || "",
          key_concerns: rec.concern && rec.concern !== "-" ? rec.concern : "none identified",
          age: Number.isFinite(parsedAge) ? parsedAge : null,
          sex: rec.sex || null,
          timestamp: exportedTimestamp,
          processed_at: rec.processedAtRaw || exportedTimestamp,
          latitude: Number.isFinite(rec.latitude) ? rec.latitude : null,
          longitude: Number.isFinite(rec.longitude) ? rec.longitude : null,
          address: rec.location || null,
          user_id: rec.userId || null,
          name: rec.enumerator || null,
          campaign_code: rec.campaignCode || null,
          audio_url: rec.audioUrl || null,
          respondent_audio_url: rec.respondentAudioUrl || null,
          created_at: rec.createdAtRaw || exportedTimestamp,
          updated_at: rec.updatedAtRaw || rec.createdAtRaw || exportedTimestamp,
          deleted_at: rec.deletedAtRaw || null,
          speaker_role_map: normalizedSpeakerRoleMap,
          identified_barangay: rec.identifiedBarangay || rec.barangay || null,
        };
      });

      const campaignCode = c.code || "recordings";
      const stamp = new Date().toISOString().replace(/[.:]/g, "-");
      const filename = `${campaignCode}_recordings_export_${stamp}.json`;
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = downloadUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(downloadUrl);
    };

    const downloadRecordingAudio = async (rec) => {
      if (!rec?.audioUrl) return;

      const downloadRecordId = rec.fieldResponseProcessedId || rec.id || "recording";
      setDownloadingRecordingId(downloadRecordId);

      const safeRecordId = String(downloadRecordId)
        .trim()
        .replace(/[^a-z0-9-_]+/gi, "_");
      const safeCampaignCode = String(rec.campaignCode || c.code || "campaign")
        .trim()
        .replace(/[^a-z0-9-_]+/gi, "_");
      const filename = `${safeRecordId || "recording"}-${safeCampaignCode || "campaign"}.mp3`;

      try {
        const response = await fetch(rec.audioUrl);
        if (!response.ok) throw new Error(`Failed to fetch audio: ${response.status}`);

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        window.URL.revokeObjectURL(downloadUrl);
        return;
      } catch (error) {
        const anchor = document.createElement("a");
        anchor.href = rec.audioUrl;
        anchor.download = filename;
        anchor.rel = "noopener noreferrer";
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
      } finally {
        setDownloadingRecordingId(null);
      }
    };

    const activeFilterChips = recsFilterGroups.flatMap((group) => {
      const selected = recsFiltersApplied[group.key] || [];
      return selected.map((value) => {
        const option = group.options.find((item) => item.value === value);
        return {
          key: group.key,
          value,
          title: group.title,
          valueLabel: option?.label || value,
        };
      });
    });

    const activeFilterCount = activeFilterChips.length;

    const recordingsPagination = resolveProcessedResponsePagination(processedResponses);
    const currentRecordingsPage = recordingsPage;
    const totalRecordingsPages = Math.max(
      1,
      recordingsPagination.totalPages ||
        currentRecordingsPage + (recordingsPagination.hasNextPage ? 1 : 0),
    );
    const paginatedRecordings = filteredRecordings;
    const canGoToNextPage =
      recordingsPagination.hasNextPage || currentRecordingsPage < totalRecordingsPages;
    const recordingsTotal = totalProcessed;
    const ageGenderMix = resolveAgeGenderMixFromAnalytics({
      ageSource: analytics?.age,
      sexSource: analytics?.sex,
      campaignTotal: recordingsTotal,
    });
    const mapRecordings = filteredRecordings.filter(
      (rec) => Number.isFinite(rec.latitude) && Number.isFinite(rec.longitude),
    );
    const googleMapsApiKey = env.GOOGLE_MAP_API_KEY || "";
    const mapCenter = mapRecordings.length
      ? { lat: mapRecordings[0].latitude, lng: mapRecordings[0].longitude }
      : { lat: 14.2167, lng: 121.1653 };
    if (mapCenterRef.current.lat !== mapCenter.lat || mapCenterRef.current.lng !== mapCenter.lng) {
      mapCenterRef.current = mapCenter;
    }
    const positiveCount = resolvedSentiment.positive;
    const negativeCount = resolvedSentiment.negative;
    const neutralCount = resolvedSentiment.neutral;
    const enumeratorPerformanceEntries = analytics
      ? resolveEnumeratorPerformanceEntries(analytics?.enumeratorPerformance)
      : d.enumeratorPerformance;
    const enumeratorPerformanceMax = Math.max(
      ...enumeratorPerformanceEntries.map((item) => Number(item?.count || 0)),
      1,
    );
    const categoryEntries = resolveDistributionEntries(analytics?.category).filter(
      (item) => item?.label != null && Number(item?.count || 0) > 0,
    );
    const employmentSectorEntries = resolveDistributionEntries(analytics?.employment).filter(
      (item) => item?.label != null && Number(item?.count || 0) > 0,
    );
    const keyConcernEntries = resolveDistributionEntries(analytics?.concerns).filter(
      (item) => item?.label != null && Number(item?.count || 0) > 0,
    );
    const renderAdditionalDistributionCard = (title, entries, color) => {
      const data = entries.slice(0, 6);
      const maxCount = getMaxEntryCount(data);

      return (
        <div className={styles.card}>
          <div className={styles.sh2} style={{ fontSize: 15, marginBottom: 12 }}>
            {title}
          </div>
          {data.length > 0 ? (
            <div className={styles.adList}>
              {data.map((item) => (
                <div key={`${title}-${item.label}`} className={styles.adRow}>
                  <div className={styles.adLabel}>{item.label}</div>
                  <div className={styles.adTrack}>
                    <div
                      className={styles.adFill}
                      style={{
                        width: `${getPercent(item.count, maxCount)}%`,
                        background: color,
                      }}
                    />
                  </div>
                  <div className={styles.adCount}>{item.count}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.infoValMuted}>No data available</div>
          )}
        </div>
      );
    };

    return (
      <div className={styles.page}>
        {analyticsLoading && (
          <div className={styles.loadingOverlay} role="status" aria-live="polite">
            <div className={styles.loadingOverlayCard}>
              <div className={styles.loadingSpinner} aria-hidden="true" />
              <div className={styles.loadingText}>Loading campaign analytics...</div>
            </div>
          </div>
        )}

        {/* Banner */}
        <div className={styles.campBanner}>
          <div className={styles.breadcrumb}>
            <button type="button" className={styles.breadcrumbLink} onClick={goBack}>
              My Campaigns
            </button>
            <svg
              viewBox="0 0 24 24"
              strokeWidth="2"
              fill="none"
              stroke="currentColor"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
            <span className={styles.breadcrumbCurrent}>{c.code}</span>
          </div>
          <div className={styles.campBannerBody}>
            <div className={styles.campCodeBadge}>{c.code}</div>
            <div className={styles.campBannerInfo}>
              <div className={styles.campBannerName}>{c.title}</div>
              <div className={styles.campBannerMeta}>
                <span>{c.location}</span>
                <span className={styles.bannerDot}>·</span>
                <span>{c.date}</span>
                <span className={styles.bannerDot}>·</span>
                <div className={styles.campBannerStatus}>
                  <div className={styles.statusDot} style={{ background: statusColor }} />
                  <span>{c.status}</span>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.htabStrip}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={
                  activeTab === tab.id ? `${styles.htab} ${styles.htabActive}` : styles.htab
                }
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Panels */}
        <div className={styles.tabPanels}>
          {/* TAB: Campaign Info */}
          {activeTab === "info" && (
            <div>
              {analyticsLoading && <div className={styles.analyticsLoader}>Loading analytics…</div>}
              {analyticsError && <div className={styles.analyticsError}>{analyticsError}</div>}
              {!campaignTypeLoading && campaignType === "custom_survey" && (
                <div className={styles.surveyGuidePanel}>
                  <div className={styles.surveyGuidePanelIcon} aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                      <path d="M5 6.5A2.5 2.5 0 0 1 7.5 4H19v14.5A1.5 1.5 0 0 0 17.5 17H7.75A2.75 2.75 0 0 0 5 19.75V6.5Z" />
                      <path d="M7.5 4A2.5 2.5 0 0 0 5 6.5V20l2.1-1.4a3 3 0 0 1 1.67-.5H17.5" />
                    </svg>
                  </div>
                  <div className={styles.surveyGuidePanelContent}>
                    <div className={styles.surveyGuidePanelTitleRow}>
                      <div className={styles.surveyGuidePanelTitle}>Custom Survey</div>
                      <span className={styles.surveyGuidePanelBadge}>Guided</span>
                    </div>
                    <div className={styles.surveyGuidePanelMeta}>{getSurveyGuideBannerMeta(c)}</div>
                  </div>
                  <button
                    type="button"
                    className={styles.surveyGuidePanelButton}
                    onClick={() => onOpenSurveyBuilder(c)}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 5h5v5" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 14 19 5" strokeLinecap="round" strokeLinejoin="round" />
                      <path
                        d="M19 13v4a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    View / Edit Survey Guide
                  </button>
                </div>
              )}
              <div className={styles.infoGrid}>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Campaign Code</div>
                  <div className={styles.infoValMono}>{c.code}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Campaign Name</div>
                  <div className={styles.infoValLg}>{c.title}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Status</div>
                  <div className={styles.infoVal} style={{ marginTop: 4 }}>
                    <span
                      className={styles.statusPill}
                      style={{
                        borderColor: `${statusColor}55`,
                        background: `${statusColor}22`,
                        color: statusColor,
                      }}
                    >
                      {c.status}
                    </span>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Campaign Start Date</div>
                  <div className={styles.infoVal}>{c.date}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Survey Type</div>
                  <div className={styles.infoVal}>{getSurveyTypeLabel(c.surveyType)}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Location</div>
                  <div className={styles.infoVal}>{c.location}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Total Recordings</div>
                  <div className={styles.infoValNum}>{recordingsTotal}</div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Sentiment Breakdown</div>
                  <div className={styles.infoVal} style={{ marginTop: 6 }}>
                    <div style={{ color: "#34d399", fontWeight: 600 }}>
                      {positiveCount} positive
                    </div>
                    <div style={{ color: "#f87171", fontWeight: 600 }}>
                      {negativeCount} negative
                    </div>
                    <div style={{ color: "#fbbf24", fontWeight: 600 }}>{neutralCount} neutral</div>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Executive Summary</div>
                  <div className={styles.infoVal} style={{ marginTop: 4 }}>
                    <span style={{ color: "rgba(191,219,254,0.4)", fontSize: 12 }}>
                      Not yet generated
                    </span>
                    <br />
                    <span style={{ color: "rgba(191,219,254,0.3)", fontSize: 11 }}>
                      End campaign to generate
                    </span>
                  </div>
                </div>
                <div className={styles.infoCard}>
                  <div className={styles.infoLbl}>Survey Document</div>
                  {c.surveyQuestionDocument ? (
                    <a
                      href={c.surveyQuestionDocument}
                      target="_blank"
                      rel="noreferrer"
                      className={styles.infoVal}
                    >
                      View document
                    </a>
                  ) : (
                    <div className={styles.infoValMuted}>No document uploaded</div>
                  )}
                </div>
              </div>

              <div className={styles.twoCol}>
                <div className={styles.card}>
                  <div className={styles.sh2}>Enumerator Performance</div>
                  <div className={styles.enumList}>
                    {enumeratorPerformanceEntries.map((en) => (
                      <div key={en.name} className={styles.enumRow}>
                        <div className={styles.enumAva}>{en.initials}</div>
                        <div className={styles.enumName}>{en.name}</div>
                        <div className={styles.enumBt}>
                          <div
                            className={styles.enumBf}
                            style={{ width: `${getPercent(en.count, enumeratorPerformanceMax)}%` }}
                          />
                        </div>
                        <div className={styles.enumCt}>{en.count}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={styles.card}>
                  <div className={styles.sh2}>Age × Gender Mix</div>
                  <div className={styles.ageGenderHeader}>
                    <span>Total respondents</span>
                    <strong>{ageGenderMix.total}</strong>
                    <span aria-hidden="true">·</span>
                    <span className={styles.ageGenderLegendItem}>
                      <span className={`${styles.ageGenderSwatch} ${styles.ageGenderMale}`} />
                      {ageGenderMix.male} male
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className={styles.ageGenderLegendItem}>
                      <span className={`${styles.ageGenderSwatch} ${styles.ageGenderFemale}`} />
                      {ageGenderMix.female} female
                    </span>
                  </div>
                  <div className={styles.ageGenderChart}>
                    {ageGenderMix.rows.map((row) => (
                      <div key={row.key} className={styles.ageGenderRow}>
                        <div className={styles.ageGenderLabel}>{row.label}</div>
                        <div className={styles.ageGenderBars}>
                          {[
                            { key: "male", count: row.male },
                            { key: "female", count: row.female },
                          ].map(({ key, count }) => (
                            <div key={key} className={styles.ageGenderBarLine}>
                              <div className={styles.ageGenderTrack}>
                                <div
                                  className={`${styles.ageGenderFill} ${styles[`ageGender${key === "male" ? "Male" : "Female"}`]}`}
                                  style={{ width: `${(count / ageGenderMix.maxCount) * 100}%` }}
                                />
                              </div>
                              <span
                                className={`${styles.ageGenderCount} ${count === 0 ? styles.ageGenderCountZero : ""}`}
                              >
                                {count}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {ageGenderMix.unclassified > 0 && (
                    <div className={styles.ageGenderNote}>
                      {ageGenderMix.unclassified} recording
                      {ageGenderMix.unclassified === 1 ? "" : "s"} not shown — age or gender not
                      recorded
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.sh2}>Additional Demographics</div>
              <div className={styles.threeCol}>
                {renderAdditionalDistributionCard("Category", categoryEntries, "#3b82f6")}
                {renderAdditionalDistributionCard(
                  "Employment Sector",
                  employmentSectorEntries,
                  "#10b981",
                )}
                {renderAdditionalDistributionCard("Key Concerns", keyConcernEntries, "#f59e0b")}
              </div>
            </div>
          )}

          {/* TAB: Executive Summary */}
          {activeTab === "exec" && (
            <div>
              <div className={styles.execContent}>
                <ExecutiveSummaryUpload campaignId={c.campaignId} />
              </div>
            </div>
          )}

          {/* TAB: Map View */}
          {activeTab === "map" && (
            <div>
              <div
                className={`${styles.filterBar} ${
                  activeFilterCount > 0 ? styles.filterBarActive : ""
                }`}
              >
                <button
                  type="button"
                  className={`${styles.btnFilter} ${
                    activeFilterCount > 0 ? styles.btnFilterActive : ""
                  }`}
                  onClick={openRecsFilterPanel}
                >
                  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="20" y2="12" />
                    <line x1="12" y1="18" x2="20" y2="18" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className={styles.filterBadge}>{activeFilterCount}</span>
                  )}
                </button>
                <div className={styles.recsActiveChips}>
                  {activeFilterChips.map((chip) => (
                    <span key={`${chip.key}-${chip.value}`} className={styles.filterChip}>
                      {chip.title}: <strong>{chip.valueLabel}</strong>
                    </span>
                  ))}
                </div>
                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    className={styles.btnClearAll}
                    onClick={clearAllRecsFilters}
                  >
                    Clear all
                  </button>
                )}
              </div>

              <div className={styles.mapLeg}>
                <div className={styles.mapLegItem}>
                  <div className={styles.mapLegDot} style={{ background: "#10b981" }} />
                  Positive
                </div>
                <div className={styles.mapLegItem}>
                  <div className={styles.mapLegDot} style={{ background: "#f59e0b" }} />
                  Neutral / Mixed
                </div>
                <div className={styles.mapLegItem}>
                  <div className={styles.mapLegDot} style={{ background: "#ef4444" }} />
                  Negative
                </div>
              </div>
              <div className={styles.mapGrid}>
                <div className={styles.mapCanvasWrap}>
                  <div className={styles.mapLblBox}>
                    <div className={styles.mapLblB}>Survey Coverage</div>
                  </div>
                  {googleMapsApiKey ? (
                    <LoadScriptNext googleMapsApiKey={googleMapsApiKey}>
                      <GoogleMap
                        onLoad={(map) => {
                          mapRef.current = map;
                          window.google.maps.event.trigger(map, "resize");
                          recenterMap();
                          updateMapScopeStats();
                        }}
                        onClick={() => setSelectedMapPin(null)}
                        onUnmount={() => {
                          mapRef.current = null;
                        }}
                        onIdle={updateMapScopeStats}
                        onDragEnd={updateMapScopeStats}
                        onZoomChanged={updateMapScopeStats}
                        mapContainerClassName={styles.mapGoogleCanvas}
                        center={mapCenterRef.current}
                        zoom={10}
                        options={{
                          disableDefaultUI: true,
                          draggable: true,
                          gestureHandling: "greedy",
                          zoomControl: true,
                          styles: [
                            { elementType: "geometry", stylers: [{ color: "#0f172a" }] },
                            { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
                            {
                              elementType: "labels.text.stroke",
                              stylers: [{ color: "#0f172a" }],
                            },
                            {
                              featureType: "road",
                              elementType: "geometry",
                              stylers: [{ color: "#1e293b" }],
                            },
                            {
                              featureType: "road",
                              elementType: "labels",
                              stylers: [{ visibility: "off" }],
                            },
                            {
                              featureType: "poi",
                              stylers: [{ visibility: "off" }],
                            },
                            {
                              featureType: "water",
                              elementType: "geometry",
                              stylers: [{ color: "#0b2443" }],
                            },
                          ],
                        }}
                      >
                        {mapRecordings.map((rec) => (
                          <Marker
                            key={rec.id}
                            position={{ lat: rec.latitude, lng: rec.longitude }}
                            title={`${rec.location} (${rec.sentiment})`}
                            icon={{
                              url: mapPinIconUrl(rec.sentiment, selectedMapPin?.id === rec.id),
                            }}
                            onClick={() => {
                              setSelectedMapPin(rec);
                              if (mapRef.current) {
                                mapRef.current.panTo({ lat: rec.latitude, lng: rec.longitude });
                                const currentZoom = Number(mapRef.current.getZoom?.() ?? 10);
                                mapRef.current.setZoom(Math.max(currentZoom, 13));
                              }
                            }}
                          />
                        ))}
                      </GoogleMap>
                    </LoadScriptNext>
                  ) : (
                    <div className={styles.mapApiNotice}>
                      Set <strong>REACT_APP_GOOGLE_MAPS_API_KEY</strong> to enable Google Map.
                    </div>
                  )}
                </div>
                <div className={styles.mapSide}>
                  <div className={styles.mapAiHeader}>
                    <div>
                      <div className={styles.mapAiTitle}>Map AI</div>
                      <div className={styles.mapAiMeta}>Scoped to current viewport</div>
                    </div>
                    <button
                      type="button"
                      className={styles.mapAiNewBtn}
                      onClick={onCreateMapChatThread}
                    >
                      New
                    </button>
                  </div>
                  <div
                    className={`${styles.mapAiScope} ${
                      mapScopeStats.visible === 0 ? styles.mapAiScopeEmpty : ""
                    }`}
                  >
                    {mapScopeStats.visible === 0 ? (
                      <>
                        <svg
                          className={styles.mapAiScopeWarningIcon}
                          viewBox="0 0 24 24"
                          strokeWidth="1.8"
                          fill="none"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M5 5.5v10.25a2.25 2.25 0 002.25 2.25h2V7.75h-2A2.25 2.25 0 005 5.5zM14.75 6h2A2.25 2.25 0 0119 8.25v10.25a2.25 2.25 0 01-2.25-2.25h-2V6zM9.25 7.75l5.5-1.75v10.25l-5.5 1.75V7.75z"
                          />
                        </svg>
                        <span className={styles.mapAiScopeWarning}>
                          No respondents in view — pan or zoom out to widen the scope.
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={styles.mapAiScopeLabel}>Scope</span>
                        <span>
                          {mapScopeStats.visible} in view / {mapScopeStats.total} total · zoom{" "}
                          {mapScopeStats.zoom.toFixed(1)}
                        </span>
                      </>
                    )}
                  </div>
                  {mapScopeStats.visible === 0 && (
                    <div className={styles.mapAiScopeGuide}>
                      Ask me about the respondents inside your current map view. Zoom or pan to
                      change what I look at.
                    </div>
                  )}
                  <div className={styles.mapAiThreads}>
                    {mapChatThreads.length === 0 ? (
                      <button
                        type="button"
                        className={styles.mapAiThread}
                        onClick={onCreateMapChatThread}
                      >
                        Start a map-scoped question
                      </button>
                    ) : (
                      mapChatThreads.slice(0, 3).map((thread) => (
                        <button
                          key={thread.id}
                          type="button"
                          className={`${styles.mapAiThread} ${thread.id === mapChatThreadId ? styles.mapAiThreadActive : ""}`}
                          onClick={() => setMapChatThreadId(thread.id)}
                        >
                          {thread.title}
                        </button>
                      ))
                    )}
                  </div>
                  <div className={styles.mapAiLog} ref={mapChatLogRef}>
                    {mapChatMessages.length === 0 ? (
                      <div className={styles.mapAiEmpty}>
                        Ask a question about the respondents currently visible on the map.
                      </div>
                    ) : (
                      mapChatMessages.map((message) => (
                        <div
                          key={message.id}
                          className={
                            message.role === "user"
                              ? styles.mapAiBubbleUser
                              : styles.mapAiBubbleAssistant
                          }
                        >
                          {message.text}
                        </div>
                      ))
                    )}
                    {mapChatTyping && <div className={styles.mapAiTyping}>Thinking...</div>}
                  </div>
                  <div className={styles.mapAiComposer}>
                    <input
                      className={styles.mapAiInput}
                      value={mapChatInput}
                      onChange={(event) => setMapChatInput(event.target.value)}
                      placeholder="Ask about this area"
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          onSendMapChat();
                        }
                      }}
                    />
                    <button type="button" className={styles.mapAiSend} onClick={onSendMapChat}>
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: Recordings */}
          {activeTab === "recs" && (
            <div className={styles.recsWrap}>
              <div className={styles.rkRow}>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Total</div>
                  <div className={styles.rkVal}>{recordingsTotal}</div>
                </div>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Positive</div>
                  <div className={styles.rkVal} style={{ color: "#34d399" }}>
                    {positiveCount}
                  </div>
                </div>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Negative</div>
                  <div className={styles.rkVal} style={{ color: "#f87171" }}>
                    {negativeCount}
                  </div>
                </div>
                <div className={styles.rkCard}>
                  <div className={styles.rkLbl}>Mixed/Neutral</div>
                  <div className={styles.rkVal} style={{ color: "#fbbf24" }}>
                    {neutralCount}
                  </div>
                </div>
              </div>

              <div
                className={`${styles.filterBar} ${
                  activeFilterCount > 0 ? styles.filterBarActive : ""
                }`}
              >
                <div className={styles.recSearchBox}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search recording ID..."
                    value={recIdSearch}
                    onChange={(e) => updateRecIdSearch(e.target.value)}
                  />
                  {recIdSearch && (
                    <button className={styles.recSearchClear} onClick={() => updateRecIdSearch("")}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className={`${styles.btnFilter} ${
                    activeFilterCount > 0 ? styles.btnFilterActive : ""
                  }`}
                  onClick={openRecsFilterPanel}
                >
                  <svg viewBox="0 0 24 24" strokeWidth="2" fill="none" stroke="currentColor">
                    <line x1="4" y1="6" x2="20" y2="6" />
                    <line x1="8" y1="12" x2="20" y2="12" />
                    <line x1="12" y1="18" x2="20" y2="18" />
                  </svg>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className={styles.filterBadge}>{activeFilterCount}</span>
                  )}
                </button>
                <div className={styles.recsActiveChips}>
                  {activeFilterChips.map((chip) => (
                    <span key={`${chip.key}-${chip.value}`} className={styles.filterChip}>
                      {chip.title}: <strong>{chip.valueLabel}</strong>
                    </span>
                  ))}
                </div>
                <div className={styles.filterActionsRight}>
                  {activeFilterCount > 0 && (
                    <button
                      type="button"
                      className={styles.btnClearAll}
                      onClick={clearAllRecsFilters}
                    >
                      Clear all
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.btnFilter}
                    onClick={exportRecordingsAsJson}
                  >
                    <svg viewBox="0 0 24 24" strokeWidth="1.8" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v9" />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m8.5 10.5 3.5 3.5 3.5-3.5"
                      />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 18h14" />
                    </svg>
                    Export JSON
                  </button>
                </div>
              </div>

              <div className={styles.recList}>
                <audio
                  ref={listAudioRef}
                  style={{ display: "none" }}
                  onPlay={() => setInlineAudioPlaying(true)}
                  onPause={() => setInlineAudioPlaying(false)}
                  onEnded={() => {
                    setInlineAudioPlaying(false);
                    setInlineAudioCurrent(0);
                  }}
                  onTimeUpdate={(e) => setInlineAudioCurrent(e.currentTarget.currentTime)}
                  onLoadedMetadata={(e) => setInlineAudioDuration(e.currentTarget.duration)}
                />
                {isRecordingsPageLoading && (
                  <div className={styles.recEmpty}>Loading recordings...</div>
                )}
                {!isRecordingsPageLoading && paginatedRecordings.length === 0 && (
                  <div className={styles.recEmpty}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className={styles.recEmptyIcon}
                    >
                      <circle cx="11" cy="11" r="8" />
                      <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <p className={styles.recEmptyTitle}>
                      {recIdSearch.trim() ? (
                        <>No recording matches &ldquo;{recIdSearch.trim()}&rdquo;</>
                      ) : (
                        "No recordings match the selected filters."
                      )}
                    </p>
                    <p className={styles.recEmptySubtitle}>
                      {recIdSearch.trim()
                        ? "Check the ID and try again."
                        : "Try adjusting or clearing the filters."}
                    </p>
                  </div>
                )}
                {paginatedRecordings.map((rec) => (
                  <div key={rec.id} className={styles.rlItem}>
                    <div style={{ marginBottom: 10 }}>
                      <div className={styles.rlLbl}>
                        {formatRecordIdLabel(rec.fieldResponseProcessedId)}
                      </div>
                    </div>
                    <div className={styles.rlGrid}>
                      {/* Location */}
                      <div>
                        <div className={styles.rlLbl}>Location</div>
                        <div className={styles.rlVal}>{rec.location}</div>
                        {rec.coordsStr && (
                          <div
                            className={styles.rlValSm}
                            style={{
                              fontFamily: "ui-monospace, monospace",
                              marginTop: 2,
                              fontSize: 11,
                            }}
                          >
                            {rec.coordsStr}
                          </div>
                        )}
                      </div>
                      {/* Date */}
                      <div>
                        <div className={styles.rlLbl}>Date</div>
                        <div className={styles.rlValSm}>{rec.date}</div>
                        {rec.time && <div className={styles.rlValSm}>{rec.time}</div>}
                      </div>
                      {/* Demographics */}
                      <div>
                        <div className={styles.rlLbl}>Demographics</div>
                        {rec.ageGroup && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Age Range:</span> {rec.ageGroup}
                          </div>
                        )}
                        {rec.sex && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Gender:</span> {rec.sex}
                          </div>
                        )}
                        {(rec.employmentSector || rec.occupation) && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Employment Sector:</span>{" "}
                            {rec.employmentSector || rec.occupation}
                          </div>
                        )}
                        {(rec.residency || rec.areaType) && (
                          <div className={styles.rlValSm}>
                            <span style={{ color: "#60a5fa" }}>Residence:</span>{" "}
                            {rec.residency || rec.areaType}
                          </div>
                        )}
                        {rec.enumerator && (
                          <div className={styles.rlValSm} style={{ marginTop: 4 }}>
                            <span style={{ color: "#60a5fa" }}>By:</span> {rec.enumerator}
                          </div>
                        )}
                      </div>
                      {/* Summary */}
                      <div>
                        <div className={styles.rlLbl}>Summary</div>
                        <div className={styles.rlValSm} style={{ lineHeight: 1.6 }}>
                          {rec.summary || "—"}
                        </div>
                      </div>
                      {/* Sentiment */}
                      <div>
                        <div className={styles.rlLbl}>Sentiment</div>
                        <span
                          className={styles.recSentBadge}
                          style={{
                            borderColor: `${sentimentColor(rec.sentiment)}55`,
                            background: `${sentimentColor(rec.sentiment)}22`,
                            color: sentimentColor(rec.sentiment),
                          }}
                        >
                          {rec.sentiment}
                        </span>
                        {rec.tone && (
                          <div className={styles.rlValSm} style={{ marginTop: 4 }}>
                            <span style={{ color: "#60a5fa" }}>Tone:</span> {rec.tone}
                          </div>
                        )}
                        <div className={styles.recActs}>
                          <button
                            type="button"
                            className={styles.btnView}
                            onClick={() => setSelectedRecording(rec)}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            View
                          </button>
                          <button
                            type="button"
                            className={styles.btnDownload}
                            onClick={() => downloadRecordingAudio(rec)}
                            disabled={!rec.audioUrl}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 3v12m0 0l4-4m-4 4l-4-4m-5 8h18"
                              />
                            </svg>
                            Download
                          </button>
                          <button
                            type="button"
                            className={styles.btnDel}
                            onClick={() => onOpenDeleteRecordingModal(rec)}
                          >
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className={styles.inlineAudio}>
                      <button
                        type="button"
                        className={styles.iPlayBtn}
                        onClick={() => toggleInlinePlay(rec)}
                        disabled={!rec.audioUrl}
                        aria-label={
                          inlineAudioPlaying && inlineAudioRecordId === rec.id ? "Pause" : "Play"
                        }
                      >
                        {inlineAudioPlaying && inlineAudioRecordId === rec.id ? (
                          <svg viewBox="0 0 24 24" fill="white">
                            <rect x="5" y="3" width="4" height="18" rx="1" />
                            <rect x="15" y="3" width="4" height="18" rx="1" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="white">
                            <polygon points="5,3 19,12 5,21" />
                          </svg>
                        )}
                      </button>
                      <div className={styles.iProgBar} onClick={(e) => seekInlineAudio(e, rec.id)}>
                        <div
                          className={styles.iProgFill}
                          style={{
                            width:
                              inlineAudioRecordId === rec.id && inlineAudioDuration > 0
                                ? `${(inlineAudioCurrent / inlineAudioDuration) * 100}%`
                                : "0%",
                          }}
                        />
                      </div>
                      <div className={styles.iDur}>
                        {inlineAudioRecordId === rec.id
                          ? `${formatAudioTime(inlineAudioCurrent)} / ${formatAudioTime(inlineAudioDuration)}`
                          : "0:00 / 0:00"}
                      </div>
                      <button
                        type="button"
                        className={styles.iStopBtn}
                        title="Stop & restart"
                        onClick={stopInlinePlay}
                        disabled={inlineAudioRecordId !== rec.id}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="4" y="4" width="16" height="16" rx="2" />
                        </svg>
                      </button>
                    </div>
                    {rec.concernChips.length > 0 && (
                      <div className={styles.kcChips}>
                        {rec.concernChips.map((chip) => (
                          <span key={chip} className={styles.kcChip}>
                            {chip}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={styles.recPagination}>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() => setRecordingsPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentRecordingsPage <= 1 || isRecordingsPageLoading}
                >
                  Previous
                </button>
                <div className={styles.pageInfo}>
                  {isRecordingsPageLoading
                    ? `Loading page ${currentRecordingsPage}...`
                    : `Page ${currentRecordingsPage} of ${totalRecordingsPages}`}
                </div>
                <button
                  type="button"
                  className={styles.pageButton}
                  onClick={() =>
                    setRecordingsPage((prev) => Math.min(totalRecordingsPages, prev + 1))
                  }
                  disabled={!canGoToNextPage || isRecordingsPageLoading}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {isRecsFilterOpen && (
            <button
              type="button"
              className={styles.fpBackdrop}
              aria-label="Close filters"
              onClick={closeRecsFilterPanel}
            />
          )}

          <div className={`${styles.fpPanel} ${isRecsFilterOpen ? styles.fpPanelOpen : ""}`}>
            <div className={styles.fpHead}>
              <div className={styles.fpTitle}>Filter Recordings</div>
              <button type="button" className={styles.fpClose} onClick={closeRecsFilterPanel}>
                ×
              </button>
            </div>
            <div className={styles.fpBody}>
              {recsFilterGroups.map((group) => {
                const selected = recsFiltersDraft[group.key] || [];
                const isAllChecked = selected.length === 0;

                return (
                  <div key={group.key} className={styles.fpGroup}>
                    <div className={styles.fpGroupTitle}>{group.title}</div>
                    <div className={styles.fpOptions}>
                      <label className={`${styles.fpOpt} ${styles.fpAllRow}`}>
                        <input
                          type="checkbox"
                          checked={isAllChecked}
                          onChange={() => toggleRecsDraftFilter(group.key, "__all__")}
                        />
                        <span className={styles.fpOptLbl}>All</span>
                      </label>
                      {group.options.map((option) => (
                        <label key={option.value} className={styles.fpOpt}>
                          <input
                            type="checkbox"
                            checked={selected.includes(option.value)}
                            onChange={() => toggleRecsDraftFilter(group.key, option.value)}
                          />
                          <span className={styles.fpOptLbl}>{option.label}</span>
                          <span className={styles.fpOptCount}>{option.count}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.fpFoot}>
              <button type="button" className={styles.fpReset} onClick={resetRecsDraftFilters}>
                Reset All
              </button>
              <button type="button" className={styles.fpApply} onClick={applyRecsFilters}>
                Apply Filters
              </button>
            </div>
          </div>

          {/* TAB: AI Chat */}
          {activeTab === "chat" && (
            <section className={styles.aiChatSection}>
              <div className={styles.aiChatShell}>
                <aside className={styles.aiChatSidebar}>
                  <div className={styles.aiChatSidebarHeader}>
                    <div>
                      <h3>Threads</h3>
                      <p>Revisit past conversations</p>
                    </div>
                    <button
                      type="button"
                      className={styles.aiChatNewThread}
                      onClick={onCreateNewChatThread}
                      disabled={chatInitializing || chatTyping}
                    >
                      New
                    </button>
                  </div>

                  <div className={styles.aiChatThreadList}>
                    {chatThreads.length === 0 && (
                      <div className={styles.aiChatThreadEmpty}>No previous threads yet.</div>
                    )}
                    {chatThreads.map((thread, index) => {
                      const threadId = thread?.insightChatThreadId;
                      const active = threadId && threadId === chatThreadId;

                      return (
                        <button
                          key={threadId || `chat-thread-${index}`}
                          type="button"
                          className={
                            active
                              ? `${styles.aiChatThreadItem} ${styles.aiChatThreadItemActive}`
                              : styles.aiChatThreadItem
                          }
                          onClick={() => onSelectChatThread(threadId)}
                          disabled={!threadId || chatInitializing || chatTyping}
                        >
                          <div className={styles.aiChatThreadTitle}>{thread.displayLabel}</div>
                          {thread.displayTime && (
                            <div className={styles.aiChatThreadMeta}>{thread.displayTime}</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </aside>

                <div className={styles.aiChatMain}>
                  <div className={styles.aiChatHeader}>
                    <div>
                      <h3>AI Campaign Assistant</h3>
                      <p>Ask about campaign insights, concerns, and suggested actions.</p>
                    </div>
                  </div>

                  <div className={styles.aiChatMessages}>
                    {chatMessages.map((message) => (
                      <div
                        key={message.id}
                        className={
                          message.role === "user"
                            ? styles.aiChatBubbleUser
                            : styles.aiChatBubbleAssistant
                        }
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: formatChatMessageHtml(message.text),
                          }}
                        />
                      </div>
                    ))}
                    {chatTyping && (
                      <div className={styles.aiChatTyping}>Assistant is typing...</div>
                    )}
                    {chatInitializing && (
                      <div className={styles.aiChatTyping}>Loading conversation...</div>
                    )}
                  </div>

                  <div className={styles.aiChatInputRow}>
                    <input
                      className={styles.aiChatInput}
                      placeholder="Ask: Give me a quick summary"
                      value={chatInput}
                      onChange={(event) => setChatInput(event.target.value)}
                      disabled={chatTyping || chatInitializing}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" && !chatTyping && !chatInitializing) {
                          event.preventDefault();
                          onSendChat();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className={styles.aiChatSend}
                      onClick={onSendChat}
                      disabled={!chatInput.trim() || chatTyping || chatInitializing}
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {selectedRecording && (
          <div
            className={styles.rdOverlay}
            role="presentation"
            onMouseDown={() => setSelectedRecording(null)}
          >
            <div
              className={styles.rdBox}
              role="dialog"
              aria-modal="true"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.rdClose}
                onClick={() => setSelectedRecording(null)}
              >
                ×
              </button>
              <div className={styles.rdContent}>
                {/* Header */}
                <h2 className={styles.rdName}>
                  {selectedRecording.respondentName ||
                    selectedRecording.enumerator ||
                    "Recording Detail"}
                </h2>

                {/* Survey Information */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Survey Information</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Location</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.location}
                        {selectedRecording.barangay && (
                          <>
                            <br />
                            <small style={{ color: "rgba(191,219,254,.55)" }}>
                              {selectedRecording.barangay}
                            </small>
                          </>
                        )}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Campaign</div>
                      <div className={styles.rdfv}>
                        <span className={styles.badgeBlue}>{selectedRecording.campaignCode}</span>
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Date &amp; Time</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.dateRaw
                          ? new Date(selectedRecording.dateRaw).toLocaleString()
                          : [selectedRecording.date, selectedRecording.time]
                              .filter(Boolean)
                              .join(", ") || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Enumerator</div>
                      <div className={styles.rdfv}>{selectedRecording.enumerator}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Sentiment</div>
                      <div className={styles.rdfv}>
                        <span
                          className={styles.recSentBadge}
                          style={{
                            borderColor: `${sentimentColor(selectedRecording.sentiment)}55`,
                            background: `${sentimentColor(selectedRecording.sentiment)}22`,
                            color: sentimentColor(selectedRecording.sentiment),
                            textTransform: "uppercase",
                          }}
                        >
                          {selectedRecording.sentiment}
                        </span>
                      </div>
                    </div>
                    {selectedRecording.tone && (
                      <div className={styles.rdf}>
                        <div className={styles.rdfl}>Tone</div>
                        <div className={styles.rdfv}>{selectedRecording.tone}</div>
                      </div>
                    )}
                  </div>
                  {selectedRecording.concernChips.length > 0 && (
                    <div style={{ marginTop: 6 }}>
                      <div className={styles.rdfl} style={{ marginBottom: 5 }}>
                        Key Concerns
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                        {selectedRecording.concernChips.map((chip) => (
                          <span key={chip} className={styles.badgePurple}>
                            {chip}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Audio */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Audio Recording</div>
                  <div className={styles.audioPl}>
                    {selectedRecording.audioUrl && (
                      <audio
                        ref={audioRef}
                        src={selectedRecording.audioUrl}
                        onPlay={() => setAudioPlaying(true)}
                        onPause={() => setAudioPlaying(false)}
                        onEnded={() => {
                          setAudioPlaying(false);
                          setAudioCurrent(0);
                        }}
                        onTimeUpdate={(e) => setAudioCurrent(e.currentTarget.currentTime)}
                        onLoadedMetadata={(e) => setAudioDuration(e.currentTarget.duration)}
                        style={{ display: "none" }}
                      />
                    )}
                    <button
                      type="button"
                      className={styles.playBtn}
                      onClick={togglePlay}
                      disabled={!selectedRecording.audioUrl}
                      aria-label={audioPlaying ? "Pause" : "Play"}
                    >
                      {audioPlaying ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <rect x="5" y="3" width="4" height="18" rx="1" />
                          <rect x="15" y="3" width="4" height="18" rx="1" />
                        </svg>
                      ) : (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <polygon points="5,3 19,12 5,21" />
                        </svg>
                      )}
                    </button>
                    <div
                      className={styles.progBar}
                      onClick={seekAudio}
                      role="progressbar"
                      aria-valuenow={audioCurrent}
                      aria-valuemax={audioDuration}
                    >
                      <div
                        className={styles.progFill}
                        style={{
                          width: audioDuration ? `${(audioCurrent / audioDuration) * 100}%` : "0%",
                        }}
                      />
                    </div>
                    <div className={styles.dur}>
                      {formatAudioTime(audioCurrent)} / {formatAudioTime(audioDuration)}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {selectedRecording.summary && (
                  <div className={styles.rdSec}>
                    <div className={styles.rdSecH}>AI-Generated Summary</div>
                    <div className={styles.aiBox}>
                      <p>{selectedRecording.summary}</p>
                    </div>
                  </div>
                )}

                {/* Transcript */}
                <div className={styles.rdSec}>
                  <div className={styles.txHeaderRow}>
                    <div className={styles.txHeaderLeft}>
                      <div className={styles.rdSecH}>
                        {isTranscriptGrouped ? "Earmarked Transcript" : "Full Transcript"}
                      </div>
                      {countEditedTranscriptLines(transcriptLines) > 0 && (
                        <span className={styles.txEditedBadge}>
                          • {countEditedTranscriptLines(transcriptLines)} line edited
                          {countEditedTranscriptLines(transcriptLines) > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>

                    <div className={styles.txActions}>
                      <button
                        type="button"
                        className={styles.txActionBtn}
                        onClick={undoTranscriptChanges}
                        disabled={
                          !transcriptHistory.length ||
                          transcriptSaveRowIndex != null ||
                          transcriptRestoreRowIndex != null ||
                          isTranscriptRestoreAllLoading
                        }
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 10h10a7 7 0 117 7h-3"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10l4-4" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10l4 4" />
                        </svg>
                        Undo
                      </button>
                      <button
                        type="button"
                        className={styles.txActionBtn}
                        onClick={() => setShowRestoreTranscriptConfirm(true)}
                        disabled={
                          countEditedTranscriptLines(transcriptLines) === 0 ||
                          transcriptSaveRowIndex != null ||
                          transcriptRestoreRowIndex != null ||
                          isTranscriptRestoreAllLoading
                        }
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3 12a9 9 0 101.64-5.2"
                          />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4v5h5" />
                        </svg>
                        Restore original
                      </button>
                    </div>
                  </div>

                  {transcriptError ? <p className={styles.txError}>{transcriptError}</p> : null}

                  <div className={styles.txBox}>
                    {transcriptLoading ? (
                      <div className={styles.txLoading}>Loading transcript...</div>
                    ) : isTranscriptGrouped ? (
                      transcriptGroupCards.length > 0 ? (
                        <div className={styles.txGroupList}>
                          {transcriptGroupCards.map((group, index) => {
                            const isOrphan = group.sequence == null;
                            const resolvedQuestionNumber =
                              group?.campaignQuestionId != null
                                ? surveyQuestionNumberByCampaignQuestionId.get(
                                    String(group.campaignQuestionId),
                                  )
                                : null;
                            const displayQuestionNumber =
                              Number.isFinite(resolvedQuestionNumber) &&
                              resolvedQuestionNumber != null
                                ? resolvedQuestionNumber
                                : Number.isFinite(group?.sequence)
                                  ? Number(group.sequence)
                                  : null;
                            const clipRange = getGroupClipRange(group);
                            const clipDisplay = getGroupClipDisplay(group);
                            const clipSource = buildClipAudioSource(
                              selectedRecording?.audioUrl,
                              group.startMs,
                              group.endMs,
                            );

                            return (
                              <div
                                key={group.id || `group-${index}`}
                                className={styles.txGroupCard}
                              >
                                <div className={styles.txGroupHeader}>
                                  <div className={styles.txGroupHeaderLeft}>
                                    <span className={styles.txGroupBadge}>
                                      {isOrphan || displayQuestionNumber == null
                                        ? "Orphan"
                                        : `Q${displayQuestionNumber}`}
                                    </span>
                                    <div>
                                      <div className={styles.txGroupPrompt}>
                                        {group.promptSnapshot || "Untitled question segment"}
                                      </div>
                                      {group.answerSnapshot ? (
                                        <div className={styles.txGroupAnswer}>
                                          {group.answerSnapshot}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>

                                  <div className={styles.txGroupTime}>
                                    {clipRange.isValid
                                      ? `${getClipDurationSeconds(group.startMs, group.endMs)}s clip`
                                      : "--"}
                                  </div>
                                </div>

                                {clipSource ? (
                                  <>
                                    <audio
                                      ref={(node) => {
                                        if (!node) {
                                          delete groupAudioRefs.current[group.id];
                                          return;
                                        }
                                        groupAudioRefs.current[group.id] = node;
                                      }}
                                      preload="metadata"
                                      src={clipSource}
                                      style={{ display: "none" }}
                                      onLoadedMetadata={(event) => {
                                        if (!clipRange.isValid) return;
                                        const player = event.currentTarget;
                                        if (
                                          player.currentTime < clipRange.start ||
                                          player.currentTime > clipRange.end
                                        ) {
                                          player.currentTime = clipRange.start;
                                        }
                                        if (activeGroupAudioId === group.id) {
                                          setGroupAudioCurrent(player.currentTime);
                                        }
                                      }}
                                      onPlay={(event) => {
                                        if (!clipRange.isValid) return;
                                        const player = event.currentTarget;
                                        if (
                                          player.currentTime < clipRange.start ||
                                          player.currentTime >= clipRange.end
                                        ) {
                                          player.currentTime = clipRange.start;
                                        }
                                        setActiveGroupAudioId(group.id);
                                        setGroupAudioPlaying(true);
                                        setGroupAudioCurrent(player.currentTime);
                                      }}
                                      onPause={() => {
                                        if (activeGroupAudioId === group.id) {
                                          setGroupAudioPlaying(false);
                                        }
                                      }}
                                      onSeeking={(event) => {
                                        if (!clipRange.isValid) return;
                                        const player = event.currentTarget;
                                        if (player.currentTime < clipRange.start) {
                                          player.currentTime = clipRange.start;
                                        }
                                        if (player.currentTime > clipRange.end) {
                                          player.currentTime = clipRange.end;
                                        }
                                      }}
                                      onTimeUpdate={(event) => {
                                        if (!clipRange.isValid) return;
                                        const player = event.currentTarget;
                                        if (player.currentTime >= clipRange.end) {
                                          player.pause();
                                          player.currentTime = clipRange.start;
                                          if (activeGroupAudioId === group.id) {
                                            setGroupAudioPlaying(false);
                                            setGroupAudioCurrent(clipRange.start);
                                          }
                                          return;
                                        }

                                        if (activeGroupAudioId === group.id) {
                                          setGroupAudioCurrent(player.currentTime);
                                        }
                                      }}
                                    />

                                    <div className={styles.txGroupPlayer}>
                                      <button
                                        type="button"
                                        className={styles.txGroupPlayBtn}
                                        onClick={() => toggleGroupClipPlay(group)}
                                        disabled={!clipRange.isValid}
                                        aria-label={
                                          activeGroupAudioId === group.id && groupAudioPlaying
                                            ? "Pause clip"
                                            : "Play clip"
                                        }
                                      >
                                        {activeGroupAudioId === group.id && groupAudioPlaying ? (
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="white"
                                          >
                                            <rect x="5" y="3" width="4" height="18" rx="1" />
                                            <rect x="15" y="3" width="4" height="18" rx="1" />
                                          </svg>
                                        ) : (
                                          <svg
                                            width="14"
                                            height="14"
                                            viewBox="0 0 24 24"
                                            fill="white"
                                          >
                                            <polygon points="5,3 19,12 5,21" />
                                          </svg>
                                        )}
                                      </button>

                                      <div
                                        className={styles.txGroupProgBar}
                                        onClick={(event) => seekGroupClip(event, group)}
                                      >
                                        <div
                                          className={styles.txGroupProgFill}
                                          style={{ width: `${clipDisplay.progress}%` }}
                                        />
                                      </div>

                                      <div className={styles.txGroupDur}>
                                        {clipDisplay.elapsedText} / {clipDisplay.durationText}
                                      </div>

                                      <button
                                        type="button"
                                        className={styles.txGroupStopBtn}
                                        onClick={() => stopGroupClip(group)}
                                        disabled={activeGroupAudioId !== group.id}
                                        title="Stop & restart"
                                      >
                                        <svg
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                        >
                                          <rect x="4" y="4" width="16" height="16" rx="2" />
                                        </svg>
                                      </button>
                                    </div>
                                  </>
                                ) : null}

                                <div className={styles.txGroupRows}>
                                  {group.rows.length > 0 ? (
                                    group.rows.map((entry) =>
                                      renderTranscriptLine({
                                        line: entry.line,
                                        rowIndex: entry.rowIndex,
                                        readOnly: entry.readOnly,
                                      }),
                                    )
                                  ) : (
                                    <div className={styles.txLoading}>
                                      No transcript rows available for this group.
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className={styles.txLoading}>
                          Grouped transcript metadata is not available for this recording.
                        </div>
                      )
                    ) : transcriptLines.length > 0 ? (
                      transcriptLines.map((line, index) =>
                        renderTranscriptLine({
                          line,
                          rowIndex: index,
                          readOnly: false,
                        }),
                      )
                    ) : (
                      <div className={styles.txLoading}>
                        Transcript unavailable for this recording.
                      </div>
                    )}
                  </div>

                  <p className={styles.txHint}>
                    {isTranscriptGrouped
                      ? "Each card maps to an earmarked question window. Grey lines are context rows and are read-only."
                      : "Click any line to correct it. Enter saves, Esc cancels. Edited lines are marked and can be reverted individually."}
                  </p>
                </div>

                {/* Basic Identity */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Basic Identity</div>
                  <div className={styles.rdGrid}>
                    <div
                      className={`${styles.rdf} ${selectedRecording.ageBandEdited ? styles.rdfEdited : ""}`}
                    >
                      <div className={styles.rdfl}>Age Group</div>
                      <div className={styles.identitySelectWrap}>
                        <select
                          className={styles.identitySelect}
                          value={
                            normalizeAgeBandValue(
                              selectedRecording?.ageBand ?? selectedRecording?.ageGroup,
                            ) ?? IDENTITY_UNKNOWN_OPTION
                          }
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            saveIdentityField({
                              field: "ageBand",
                              value: nextValue === IDENTITY_UNKNOWN_OPTION ? null : nextValue,
                            });
                          }}
                          disabled={
                            identitySavingField === "ageBand" || identitySavingField === "sex"
                          }
                        >
                          {AGE_BAND_OPTIONS.map((option) => (
                            <option
                              key={String(option.value)}
                              value={option.value == null ? IDENTITY_UNKNOWN_OPTION : option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedRecording.ageBandEdited ? (
                        <div className={styles.identityEditedLabel}>
                          Corrected - was {formatAgeBandLabel(selectedRecording.originalAgeBand)}
                        </div>
                      ) : null}
                    </div>
                    <div
                      className={`${styles.rdf} ${selectedRecording.sexEdited ? styles.rdfEdited : ""}`}
                    >
                      <div className={styles.rdfl}>Gender</div>
                      <div className={styles.identitySelectWrap}>
                        <select
                          className={styles.identitySelect}
                          value={
                            normalizeSexValue(selectedRecording?.sex) ?? IDENTITY_UNKNOWN_OPTION
                          }
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            saveIdentityField({
                              field: "sex",
                              value: nextValue === IDENTITY_UNKNOWN_OPTION ? null : nextValue,
                            });
                          }}
                          disabled={
                            identitySavingField === "ageBand" || identitySavingField === "sex"
                          }
                        >
                          {SEX_OPTIONS.map((option) => (
                            <option
                              key={String(option.value)}
                              value={option.value == null ? IDENTITY_UNKNOWN_OPTION : option.value}
                            >
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedRecording.sexEdited ? (
                        <div className={styles.identityEditedLabel}>
                          Corrected - was {formatSexLabel(selectedRecording.originalSex)}
                        </div>
                      ) : null}
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Civil Status</div>
                      <div className={styles.rdfv}>{selectedRecording.civilStatus || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Dependents</div>
                      <div className={styles.rdfv}>{selectedRecording.dependents || "-"}</div>
                    </div>
                  </div>
                  {identitySavingField ? (
                    <div className={styles.identitySaving}>Saving identity update...</div>
                  ) : null}
                  {identityError ? (
                    <div className={styles.identityError}>{identityError}</div>
                  ) : null}
                </div>

                {/* Residency & Socioeconomic */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Residency &amp; Socioeconomic</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Years in Area</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.yearsInArea || selectedRecording.residency || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Area Type (Urban/Rural)</div>
                      <div className={styles.rdfv}>{selectedRecording.areaType || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Social Class</div>
                      <div className={styles.rdfv}>{selectedRecording.socialClass || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Employment Sector</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.employmentSector || selectedRecording.occupation || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Monthly Income</div>
                      <div className={styles.rdfv}>{selectedRecording.monthlyIncome || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Housing Type</div>
                      <div className={styles.rdfv}>{selectedRecording.housing || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Media & Information */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Media &amp; Information</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Primary Platform</div>
                      <div className={styles.rdfv}>{selectedRecording.primaryPlatform || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Religion & Culture */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Religion &amp; Culture</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Religion</div>
                      <div className={styles.rdfv}>{selectedRecording.religion || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Ethnicity / Language</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.ethnicityLanguage || "-"}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Civic & Political */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Civic &amp; Political</div>
                  <div className={styles.rdGrid}>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Voter Status</div>
                      <div className={styles.rdfv}>{selectedRecording.voterStatus || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Vote Intent</div>
                      <div className={styles.rdfv}>{selectedRecording.voteIntent || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Vote Intent Strength</div>
                      <div className={styles.rdfv}>
                        {selectedRecording.voteIntentStrength || "-"}
                      </div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>National Gov Rating</div>
                      <div className={styles.rdfv}>{selectedRecording.nationalGov || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Local Gov - Governor Rating</div>
                      <div className={styles.rdfv}>{selectedRecording.governor || "-"}</div>
                    </div>
                    <div className={styles.rdf}>
                      <div className={styles.rdfl}>Local Gov - Mayor Rating</div>
                      <div className={styles.rdfv}>{selectedRecording.mayor || "-"}</div>
                    </div>
                  </div>
                </div>

                {/* Leader Preferences */}
                <div className={styles.rdSec}>
                  <div className={styles.rdSecH}>Leader Preferences</div>
                  <div style={{ marginBottom: 8 }}>
                    <div className={styles.rdfl}>
                      Core Values:{" "}
                      <span style={{ color: "#b0bcd4" }}>
                        {selectedRecording.coreValues || "-"}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div className={styles.rdfl}>
                      Leader Archetype Preference:{" "}
                      <span style={{ color: "#b0bcd4" }}>
                        {selectedRecording.leaderArchetype || "-"}
                      </span>
                    </div>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div className={styles.rdfl} style={{ marginBottom: 5 }}>
                      Desired Traits
                    </div>
                    {selectedRecording.desiredTraits.length > 0 ? (
                      <div className={styles.traitRow}>
                        {selectedRecording.desiredTraits.map((t) => (
                          <span key={t} className={styles.traitD}>
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.rdfv}>-</div>
                    )}
                  </div>
                  <div>
                    <div className={styles.rdfl} style={{ marginBottom: 5 }}>
                      Rejected Traits
                    </div>
                    {selectedRecording.rejectedTraits.length > 0 ? (
                      <div className={styles.traitRow}>
                        {selectedRecording.rejectedTraits.map((t) => (
                          <span key={t} className={styles.traitR}>
                            {t}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className={styles.rdfv}>-</div>
                    )}
                  </div>
                </div>
              </div>

              {showRestoreTranscriptConfirm && (
                <div
                  className={styles.txRestoreOverlay}
                  role="presentation"
                  onMouseDown={() => setShowRestoreTranscriptConfirm(false)}
                >
                  <div
                    className={styles.txRestoreModal}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="restore-transcript-title"
                    onMouseDown={(event) => event.stopPropagation()}
                  >
                    <div className={styles.txRestoreIcon} aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M3 12a9 9 0 101.64-5.2"
                        />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 4v5h5" />
                      </svg>
                    </div>
                    <h3 id="restore-transcript-title">Restore original transcript?</h3>
                    <p>
                      This discards {countEditedTranscriptLines(transcriptLines)} edited line
                      {countEditedTranscriptLines(transcriptLines) > 1 ? "s" : ""} and returns the
                      transcript to the version produced by transcription. Identity corrections are
                      not affected.
                    </p>
                    <div className={styles.txRestoreActions}>
                      <button
                        type="button"
                        className={styles.txRestoreKeepBtn}
                        onClick={() => setShowRestoreTranscriptConfirm(false)}
                        disabled={isTranscriptRestoreAllLoading}
                      >
                        Keep my edits
                      </button>
                      <button
                        type="button"
                        className={styles.txRestoreConfirmBtn}
                        onClick={restoreOriginalTranscript}
                        disabled={isTranscriptRestoreAllLoading}
                      >
                        {isTranscriptRestoreAllLoading ? "Restoring..." : "Restore original"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {transcriptToast && <div className={styles.txToast}>{transcriptToast}</div>}
            </div>
          </div>
        )}

        {isDeleteRecordingConfirmOpen && recordingToDelete && (
          <div
            className={styles.deleteRecordingOverlay}
            role="presentation"
            onMouseDown={onCloseDeleteRecordingModal}
          >
            <div
              className={styles.deleteRecordingModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-recording-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className={styles.deleteRecordingHeader}>
                <div className={styles.deleteRecordingIconWrap} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <div>
                  <h3 id="delete-recording-title" className={styles.deleteRecordingTitle}>
                    Delete Recording
                  </h3>
                  <p className={styles.deleteRecordingMessage}>
                    You are about to delete recording{" "}
                    {formatRecordIdValue(recordingToDelete.fieldResponseProcessedId)}.
                  </p>
                </div>
              </div>

              <div className={styles.deleteRecordingWarningBox}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="9" />
                  <line x1="12" y1="8" x2="12" y2="13" />
                  <circle cx="12" cy="16.5" r="1" />
                </svg>
                <p>
                  This will remove this recording from any insights or reports generated for this
                  campaign.
                </p>
              </div>

              <div className={styles.deleteRecordingActions}>
                <button
                  type="button"
                  className={styles.deleteRecordingCancelButton}
                  onClick={onCloseDeleteRecordingModal}
                  disabled={isDeletingRecording}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.deleteRecordingConfirmButton}
                  onClick={onConfirmDeleteRecording}
                  disabled={isDeletingRecording}
                >
                  {isDeletingRecording ? "Deleting..." : "Delete Recording"}
                </button>
              </div>
              {deleteRecordingError && (
                <p className={styles.deleteRecordingError}>{deleteRecordingError}</p>
              )}
            </div>
          </div>
        )}

        {isDeleteRecordingSuccessOpen && (
          <div
            className={styles.deleteRecordingOverlay}
            role="presentation"
            onMouseDown={onCloseDeleteRecordingSuccessModal}
          >
            <div
              className={styles.deleteRecordingSuccessModal}
              role="dialog"
              aria-modal="true"
              aria-labelledby="delete-recording-success-title"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className={styles.deleteRecordingSuccessIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>

              <h3
                id="delete-recording-success-title"
                className={styles.deleteRecordingSuccessTitle}
              >
                Recording Deleted
              </h3>
              <p className={styles.deleteRecordingSuccessMessage}>
                The recording has been successfully removed from this campaign.
              </p>

              <button
                type="button"
                className={styles.deleteRecordingDoneButton}
                onClick={onCloseDeleteRecordingSuccessModal}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {endCampaignModal}
      </div>
    );
  }

  /* ── LIST VIEW ── */
  return (
    <div className={styles.page}>
      <div className={styles.headerRow}>
        <div>
          <h1>My Campaigns</h1>
          <p>Select a campaign to view its detailed data, or create a new one</p>
        </div>
        <button className={styles.createButton} type="button" onClick={onOpenCreateModal}>
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New Campaign
        </button>
      </div>

      <section className={styles.statsGrid}>
        {statCards.map((card) => (
          <article key={card.label} className={styles.statCard}>
            <p>{card.label}</p>
            <strong className={styles[card.tone]}>
              {card.label === "Total Campaigns"
                ? campaigns.length
                : card.label === "Active"
                  ? campaigns.filter((campaign) => campaign.status === "active").length
                  : card.label === "Total Recordings"
                    ? totalRecordingsFromApi
                    : card.label === "Enumerators"
                      ? totalEnumeratorsFromList
                      : card.value}
            </strong>
          </article>
        ))}
      </section>

      {campaignsLoading && <div className={styles.analyticsLoader}>Loading campaigns...</div>}
      {campaignsError && <div className={styles.analyticsError}>{campaignsError}</div>}

      <section className={styles.campaignList}>
        {campaigns.map((campaign) => (
          <article key={campaign.code} className={styles.campaignCard}>
            <div className={styles.campHd}>
              <div>
                <div className={styles.identityRow}>
                  <span className={styles.code}>{campaign.code}</span>
                  <span className={styles.status}>{campaign.status}</span>
                </div>
                <h3 className={styles.campTitle}>{campaign.title}</h3>
                <p className={styles.campSub}>
                  {campaign.location}&nbsp;&nbsp;·&nbsp;&nbsp;{campaign.date}
                </p>
              </div>
              <div className={styles.campActs}>
                <button
                  type="button"
                  className={styles.endButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenEndCampaignModal(campaign);
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <rect x="9" y="9" width="6" height="6" />
                  </svg>
                  End
                </button>
                <button
                  type="button"
                  className={styles.openButton}
                  onClick={() => openCampaign(campaign)}
                >
                  Open Campaign
                  <svg
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    fill="none"
                    stroke="currentColor"
                    aria-hidden="true"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
            {(() => {
              const recCount = campaignRecordingTotals[campaign.code] ?? 0;
              const demog = campaignListDemographics[campaign.code];
              const sexEntries = resolveDistributionEntries(demog?.sex);
              const ageEntries = resolveAgeDistributionEntries(demog?.age)
                .sort((a, b) => b.count - a.count)
                .slice(0, 4);
              const maleCount =
                sexEntries.find((e) => String(e.label).toLowerCase().startsWith("m"))?.count ?? 0;
              const femaleCount =
                sexEntries.find((e) => String(e.label).toLowerCase().startsWith("f"))?.count ?? 0;
              const genderTotal = maleCount + femaleCount || 1;
              const circumference = 87.96;
              const maleDash = ((maleCount / genderTotal) * circumference).toFixed(1);
              const ageMax = ageEntries.reduce((m, e) => Math.max(m, e.count), 1);
              return (
                <div className={styles.campBody}>
                  <div className={styles.campCounts}>
                    <div>
                      <div className={styles.cmetaLbl}>Recordings</div>
                      <div className={styles.cmetaBigVal}>{recCount}</div>
                    </div>
                    <div>
                      <div className={styles.cmetaLbl}>Enumerators</div>
                      <div className={styles.cmetaBigVal}>
                        {campaignListEnumeratorCounts[campaign.code] ?? campaign.enumerators ?? 0}
                      </div>
                    </div>
                  </div>
                  <div className={styles.campDemog}>
                    <div className={styles.cmetaLbl} style={{ marginBottom: 6 }}>
                      Respondent Demographics
                    </div>
                    <div className={styles.campDemogGrid}>
                      <div className={styles.campGenderRow}>
                        <svg width="44" height="44" viewBox="0 0 44 44" style={{ flexShrink: 0 }}>
                          <circle
                            cx="22"
                            cy="22"
                            r="14"
                            fill="none"
                            stroke="#f472b6"
                            strokeWidth="9"
                          />
                          <circle
                            cx="22"
                            cy="22"
                            r="14"
                            fill="none"
                            stroke="#3b82f6"
                            strokeWidth="9"
                            strokeDasharray={`${maleDash} ${circumference}`}
                            transform="rotate(-90 22 22)"
                          />
                          <circle cx="22" cy="22" r="9" fill="#111827" />
                        </svg>
                        <div>
                          <div className={styles.cmetaLbl} style={{ marginBottom: 4 }}>
                            Gender
                          </div>
                          <div className={styles.genderLegend}>
                            <span className={styles.gLegMale}>
                              <span className={styles.gDotMale} />
                              {maleCount} Male
                            </span>
                            <span className={styles.gLegFemale}>
                              <span className={styles.gDotFemale} />
                              {femaleCount} Female
                            </span>
                          </div>
                        </div>
                      </div>
                      {ageEntries.length > 0 && (
                        <div>
                          <div className={styles.cmetaLbl} style={{ marginBottom: 5 }}>
                            Age groups
                          </div>
                          {ageEntries.map((e) => (
                            <div key={e.label} className={styles.ageBarRow}>
                              <div className={styles.ageBarLbl}>{e.label}</div>
                              <div className={styles.ageBarTrack}>
                                <div
                                  className={styles.ageBarFill}
                                  style={{ width: `${(e.count / ageMax) * 100}%` }}
                                />
                              </div>
                              <div className={styles.ageBarCount}>{e.count}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </article>
        ))}
      </section>

      {endCampaignModal}
      {createModal}
    </div>
  );
};

export default CampaignsPage;
